import type {
  ClarificationCategory,
  ClarificationAnswer,
  ClarificationMetadata,
  ClarificationOption,
  ClarificationResponsePayload,
  CompositeGenerationResponse,
  QueryResponse,
  AccessibleSchema,
} from '@/services/api/dataApi';
import type { CompositeJoinKey, CompositeQueryDefinition, TimeGranularity } from '@/types/chartDataSource';

export type CreateResultTypeLabel = 'Single Source' | 'Combined Sources' | 'Period Comparison' | 'Needs Clarification' | 'Needs Review';
export type CreateResultShapeLabel = Exclude<CreateResultTypeLabel, 'Needs Clarification' | 'Needs Review'>;
export type CreateResultIntentCategory =
  | 'single_metric_over_time'
  | 'multi_metric_same_source'
  | 'multi_source_combined'
  | 'period_comparison'
  | 'derived_metric'
  | 'needs_clarification'
  | 'needs_review';
export type CreateResultRequestedIntentCategory = Exclude<CreateResultIntentCategory, 'needs_clarification' | 'needs_review'>;

export interface CreateResultMetadata {
  resultTypeLabel: CreateResultTypeLabel;
  shapeLabel?: CreateResultShapeLabel;
  intentCategory: CreateResultIntentCategory;
  requestedIntentCategory?: CreateResultRequestedIntentCategory;
  needsReview: boolean;
  needsClarification: boolean;
  reviewAcceptanceAllowed?: boolean;
  reviewReason?: string;
  clarificationReason?: string;
  saveDisabledReason?: string;
  deterministicMetricStatusSynthesis?: MetricStatusSynthesisDiagnostic;
}

export type CreateTemplateResult =
  | {
      kind: 'template';
      response: QueryResponse;
      metadata: CreateResultMetadata;
    }
  | {
      kind: 'composite';
      explanation: string;
      definition: CompositeQueryDefinition;
      previewRows: Record<string, unknown>[];
      metadata: CreateResultMetadata;
    }
  | {
      kind: 'clarification';
      response: QueryResponse;
      clarification: ClarificationMetadata;
      metadata: CreateResultMetadata;
    };

type Settled<T> =
  | {
      status: 'fulfilled';
      value: T;
    }
  | {
      status: 'rejected';
      reason: unknown;
    };

export interface CreateTemplateGenerationDependencies {
  generateSingleSource: (prompt: string) => Promise<QueryResponse>;
  generateComposite: (prompt: string) => Promise<CompositeGenerationResponse>;
  previewComposite: (definition: CompositeQueryDefinition) => Promise<Record<string, unknown>[]>;
}

export interface CreateTemplateGenerationOptions {
  clarificationDepth?: number;
  maxClarificationDepth?: number;
}

export interface CreateClarificationResumeAnswer {
  value: string;
  label?: string;
  optionId?: string;
  isFreeText?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function getSafePreviewErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown preview error';
}

function getSafeGenerationErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (isRecord(error) && typeof error.message === 'string') {
    return error.message;
  }

  return 'Generation failed';
}

const INTERNAL_ROUTING_MARKER_PATTERN = /^\s*([A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+):\s*/;
const GENERIC_REVIEW_EXPLANATION = 'This generated template needs review before it can be saved.';
const GENERIC_RECORD_FALLBACK_REVIEW_REASON =
  'The generated template only returns a raw record instead of the requested fields. Review or rebuild it before saving.';
const COMBINED_SOURCE_SINGLE_FALLBACK_REVIEW_REASON =
  'A combined-source result could not be generated. No preview is shown because the fallback may omit requested fields such as status or date/time. Review or rebuild before saving.';
const AI_COMPOSITE_MISSING_SOURCE_ORG_REVIEW_REASON =
  'The generated multi-source template is missing an explicit source organization for at least one source. Review or rebuild it from trusted source selections before saving.';
const AI_COMPOSITE_UNTRUSTED_SOURCE_ORG_REVIEW_REASON =
  'The generated multi-source template includes AI-supplied source organization identity. Review or rebuild it from trusted source selections before saving.';
const NUMERIC_METRIC_TERM_PATTERN =
  /\b(level|depth|pressure|flow|gallons|temperature|usage|count|runtime|value|volume|amount|total|rate|percent|percentage)\b/i;
const CATEGORICAL_STATUS_TERM_PATTERN =
  /\b(status|state|mode|condition|enabled|active|running|open|closed|valve)\b/i;
const FORBIDDEN_DYNAMIC_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const METRIC_STATUS_DEBUG_SAMPLE_KEY_LIMIT = 200;
const RAW_OBJECT_ID_PATTERN = /\b[a-f0-9]{24}\b/gi;
const METRIC_STATUS_CREATE_LOG_PREFIX = '[DataDiscoveryModal:create metricStatus]';

function stripInternalRoutingMarkers(text: string): {
  text: string;
  removedMarkers: string[];
} {
  let cleaned = text.trim();
  const removedMarkers: string[] = [];

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const match = cleaned.match(INTERNAL_ROUTING_MARKER_PATTERN);
    if (!match) {
      break;
    }

    removedMarkers.push(match[1]);
    cleaned = cleaned.slice(match[0].length).trim();
  }

  return { text: cleaned, removedMarkers };
}

function cleanUserFacingModelText(text: string | undefined, emptyAfterMarkerFallback?: string): string {
  const rawText = text?.trim() ?? '';
  if (!rawText) {
    return '';
  }

  const cleaned = stripInternalRoutingMarkers(rawText);
  if (cleaned.text) {
    return cleaned.text;
  }

  return cleaned.removedMarkers.length > 0 ? emptyAfterMarkerFallback ?? '' : '';
}

function getReviewReasonFromExplanationMarker(explanation: string | undefined): string | undefined {
  const rawText = explanation?.trim();
  if (!rawText) {
    return undefined;
  }

  const cleaned = stripInternalRoutingMarkers(rawText);
  const hasReviewMarker = cleaned.removedMarkers.some((marker) => marker.includes('REVIEW'));
  if (!hasReviewMarker) {
    return undefined;
  }

  return cleaned.text || undefined;
}

function cleanQueryResponseUserText(response: QueryResponse): QueryResponse {
  if (response.answer === undefined) {
    return response;
  }

  return {
    ...response,
    answer: cleanUserFacingModelText(response.answer),
  };
}

function normalizeOptionalString(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

function formatGeneratedFieldLabel(field: string): string {
  const normalized = field
    .replace(/^data\./, '')
    .replace(/[_-]+/g, ' ')
    .trim();

  return normalized || field;
}

function getAiCompositeSourceIdentityReviewReason(definition: CompositeQueryDefinition): string | null {
  return definition.sources.some((source) => !normalizeOptionalString(source.query.organizationId))
    ? AI_COMPOSITE_MISSING_SOURCE_ORG_REVIEW_REASON
    : AI_COMPOSITE_UNTRUSTED_SOURCE_ORG_REVIEW_REASON;
}

function mergeReviewReasons(primary: string | undefined, secondary: string | undefined): string | undefined {
  if (!primary) {
    return secondary;
  }

  if (!secondary || secondary === primary || primary.includes(secondary)) {
    return primary;
  }

  if (secondary.includes(primary)) {
    return secondary;
  }

  return `${primary} ${secondary}`;
}

function getPrimaryCompositeMeasureField(definition: CompositeQueryDefinition): string | null {
  for (const source of definition.sources) {
    const measure = source.query.measures?.[0];
    if (measure?.field) {
      return formatGeneratedFieldLabel(measure.field);
    }
  }

  return null;
}

function definitionGroupsByDevice(definition: CompositeQueryDefinition): boolean {
  return definition.sources.some((source) =>
    (source.query.groupBy ?? []).some((group) => {
      const field = typeof group === 'string' ? group : group.field;
      return /\bdevice\s*id\b/i.test(formatGeneratedFieldLabel(field));
    }),
  );
}

function promptSuggestsWaterFlowChange(prompt: string): boolean {
  return /\bchange\b/i.test(prompt) && /\bwater\s+flow\b/i.test(prompt);
}

function polishCompositeReviewReason(
  reviewReason: string | undefined,
  definition: CompositeQueryDefinition,
  prompt: string,
): string | undefined {
  const cleanedReason = cleanUserFacingModelText(reviewReason);
  if (!cleanedReason) {
    return undefined;
  }

  if (
    (
      /\busage metric not explicitly present in schema fields\b/i.test(cleanedReason) ||
      /\bno explicit device or metric field ['"]?usage['"]? matched\b/i.test(cleanedReason) ||
      /\bno explicit metric or device specified and ['"]?usage['"]? has no direct field match\b/i.test(cleanedReason)
    ) &&
    /\busage\b/i.test(prompt)
  ) {
    const assumedMetric = getPrimaryCompositeMeasureField(definition);
    return assumedMetric
      ? `The prompt says "usage"; this result assumes ${assumedMetric} is the usage metric. Review before saving.`
      : 'The prompt says "usage", but the matching usage field is ambiguous. Review before saving.';
  }

  if (
    promptSuggestsWaterFlowChange(prompt) &&
    definitionGroupsByDevice(definition) &&
    (
      /\bno explicit metric or device specified\b/i.test(cleanedReason) ||
      /\bno specific device requested\b/i.test(cleanedReason)
    )
  ) {
    return 'Unclear which flow metric (gallons/cuft/pulses) and aggregation to use for change.';
  }

  return cleanedReason;
}

function settle<T>(promise: Promise<T>): Promise<Settled<T>> {
  return promise.then(
    (value) => ({ status: 'fulfilled', value }),
    (reason) => ({ status: 'rejected', reason }),
  );
}

function getFulfilledValue<T>(result: Settled<T>): T | null {
  return result.status === 'fulfilled' ? result.value : null;
}

function getRejectedMessage<T>(result: Settled<T>): string | null {
  return result.status === 'rejected' ? getSafeGenerationErrorMessage(result.reason) : null;
}

function getQueryResponseErrorMessage(response: QueryResponse): string | null {
  if (!response.error) {
    return null;
  }

  const suggestion = typeof response.suggestion === 'string' ? response.suggestion.trim() : '';
  if (!suggestion || suggestion === response.error) {
    return response.error;
  }

  return `${response.error} ${suggestion}`;
}

function responseHasMissingMeasureError(response: QueryResponse): boolean {
  const text = [response.error, response.suggestion]
    .filter((value): value is string => typeof value === 'string')
    .join(' ');

  return /\bquery\s+must\s+include\s+at\s+least\s+one\s+measure\b/i.test(text);
}

function hasTemplateQuery(
  response: QueryResponse | null,
): response is QueryResponse & { templateQuery: NonNullable<QueryResponse['templateQuery']> } {
  return response?.templateQuery !== undefined;
}

function hasQueryResponseError(response: QueryResponse | null): boolean {
  return typeof response?.error === 'string' && response.error.length > 0;
}

function hasCompositeResponse(
  response: CompositeGenerationResponse | null,
): response is CompositeGenerationResponse {
  return response !== null;
}

function responseNeedsClarification(response: QueryResponse | null): boolean {
  return Boolean(
    response &&
      (response.needsSourceSelection ||
        response.clarification ||
        response.templateIntent?.needsClarification === true ||
        response.templateIntent?.category === 'needs_clarification'),
  );
}

function compositeNeedsClarification(response: CompositeGenerationResponse | null): boolean {
  return Boolean(response && (response.needsClarification === true || response.clarification));
}

function getClarificationCategory(response: QueryResponse): ClarificationCategory {
  if (response.clarification) {
    return response.clarification.category;
  }

  return response.needsSourceSelection ? 'source_selection' : 'intent';
}

function getClarificationOptions(response: QueryResponse): ClarificationOption[] {
  if (response.clarification && response.clarification.options.length > 0) {
    return response.clarification.options;
  }

  return (response.availableSources ?? []).map((source) => ({
    id: source.id,
    label: source.name,
    value: source.id,
    confidence: source.confidence,
    reasoning: source.reasoning,
    description: source.reasoning,
  }));
}

function getClarificationQuestion(response: QueryResponse, category: ClarificationCategory): string {
  if (response.clarification) {
    return response.clarification.question;
  }

  if (typeof response.answer === 'string' && response.answer.trim()) {
    return response.answer;
  }

  if (typeof response.suggestion === 'string' && response.suggestion.trim()) {
    return response.suggestion;
  }

  return category === 'source_selection'
    ? 'Which data source should this template use?'
    : 'What should the template builder clarify before generating this template?';
}

function normalizeClarificationMetadata(response: QueryResponse, prompt: string): ClarificationMetadata {
  const category = getClarificationCategory(response);
  const options = getClarificationOptions(response);
  const availableSourceIds = options.map((option) => option.value);
  const originalPrompt = response.clarification?.originalPrompt ?? prompt;
  const ambiguityReason =
    response.clarification?.ambiguityReason ??
    response.templateIntent?.clarificationReason ??
    (category === 'source_selection'
      ? 'The request needs a source choice before it can become a reusable template.'
      : 'The request needs clarification before it can become a reusable template.');
  const requestedCategory = getBackendRequestedIntentCategory(response) ?? getRequestedIntentCategory(prompt, response);
  const resumeContext = response.clarification?.resumeContext ?? {
    originalPrompt,
    clarificationType: category,
    requestedCategory,
    availableSourceIds,
  };

  return {
    question: getClarificationQuestion(response, category),
    category,
    type: response.clarification?.type ?? category,
    options,
    allowsFreeText: response.clarification?.allowsFreeText ?? true,
    originalPrompt,
    ambiguityReason,
    resumeContext: {
      ...resumeContext,
      availableSourceIds: resumeContext.availableSourceIds ?? availableSourceIds,
    },
  };
}

function normalizeCompositeClarificationMetadata(
  response: CompositeGenerationResponse,
  prompt: string,
): ClarificationMetadata {
  const category = response.clarification?.category ?? 'intent';
  const options = response.clarification?.options ?? [];
  const originalPrompt = response.clarification?.originalPrompt ?? prompt;
  const availableSourceIds = options.map((option) => option.value);
  const requestedCategory =
    response.clarification?.resumeContext.requestedCategory ?? getRequestedIntentCategory(prompt);
  const ambiguityReason =
    response.clarification?.ambiguityReason ??
    response.clarificationReason ??
    'The request needs clarification before it can become a reusable template.';
  const resumeContext = response.clarification?.resumeContext ?? {
    originalPrompt,
    clarificationType: category,
    requestedCategory,
    availableSourceIds,
  };

  return {
    question:
      response.clarification?.question ??
      response.clarificationReason ??
      'What should the template builder clarify before generating this template?',
    category,
    type: response.clarification?.type ?? category,
    options,
    allowsFreeText: response.clarification?.allowsFreeText ?? true,
    originalPrompt,
    ambiguityReason,
    resumeContext: {
      ...resumeContext,
      availableSourceIds: resumeContext.availableSourceIds ?? availableSourceIds,
    },
  };
}

function createCompositeClarificationQueryResponse(
  clarification: ClarificationMetadata,
): QueryResponse {
  return {
    success: false,
    answer: clarification.question,
    data: [],
    clarification,
    templateIntent: {
      category: 'needs_clarification',
      requestedCategory: clarification.resumeContext.requestedCategory,
      needsReview: false,
      needsClarification: true,
      clarificationReason: clarification.ambiguityReason,
      clarification,
    },
  };
}

function getClarificationResultMetadata(clarification: ClarificationMetadata): CreateResultMetadata {
  return {
    resultTypeLabel: 'Needs Clarification',
    intentCategory: 'needs_clarification',
    requestedIntentCategory: clarification.resumeContext.requestedCategory,
    needsReview: false,
    needsClarification: true,
    clarificationReason: clarification.ambiguityReason,
    saveDisabledReason: 'Answer the clarification question before saving this template.',
  };
}

function promptHasSpecificComparisonTimeRange(text: string): boolean {
  const normalized = text.toLowerCase();
  return /\b(today|yesterday|current|previous|prior|last|past|this|hour|day|week|month|24h|24)\b/.test(normalized);
}

function promptHasChangeBaseline(text: string): boolean {
  const normalized = text.toLowerCase();
  return (
    /\b(from|against|versus|vs\.?|compared\s+to|previous|prior|yesterday|last)\b/.test(normalized) &&
    promptHasSpecificComparisonTimeRange(normalized)
  );
}

function promptNeedsUsageChangeBaselineClarification(text: string): boolean {
  const normalized = text.toLowerCase();
  const changeSignal = /\b(change|changes|changed|difference|delta|percent|percentage)\b/.test(normalized);
  const usageSignal = /\b(usage|consumption|water\s+flow)\b/.test(normalized);
  return changeSignal && usageSignal && !promptHasChangeBaseline(normalized);
}

function getChangeBaselineSubject(prompt: string): string {
  return /\bwater\s+flow\b/i.test(prompt) ? 'Water Flow' : 'usage';
}

function createUsageChangeBaselineClarification(prompt: string): ClarificationMetadata | null {
  if (!promptNeedsUsageChangeBaselineClarification(prompt)) {
    return null;
  }

  const subject = getChangeBaselineSubject(prompt);

  return {
    question: `What baseline should the ${subject} change be measured against?`,
    category: 'time_range',
    type: 'time_range',
    options: [
      {
        id: 'today_vs_yesterday',
        label: 'Today vs yesterday',
        value: 'today_vs_yesterday',
        description: 'Compare today so far with yesterday.',
      },
      {
        id: 'current_24h_vs_previous_24h',
        label: 'Current 24h vs previous 24h',
        value: 'current_24h_vs_previous_24h',
        description: 'Compare the rolling current 24 hours with the prior 24 hours.',
      },
      {
        id: 'this_week_vs_last_week',
        label: 'This week vs last week',
        value: 'this_week_vs_last_week',
        description: 'Compare the current week with the previous week.',
      },
    ],
    allowsFreeText: true,
    originalPrompt: prompt,
    ambiguityReason: `The prompt asks for change in ${subject} but does not provide a baseline period.`,
    resumeContext: {
      originalPrompt: prompt,
      clarificationType: 'time_range',
      requestedCategory: 'period_comparison',
      availableSourceIds: ['today_vs_yesterday', 'current_24h_vs_previous_24h', 'this_week_vs_last_week'],
    },
  };
}

function createDeterministicClarificationQueryResponse(clarification: ClarificationMetadata): QueryResponse {
  return {
    success: true,
    answer: clarification.question,
    data: [],
    clarification,
    templateIntent: {
      category: 'needs_clarification',
      requestedCategory: clarification.resumeContext.requestedCategory,
      needsReview: false,
      needsClarification: true,
      clarificationReason: clarification.ambiguityReason,
      clarification,
    },
  };
}

function getClarificationLoopReviewMetadata(clarification: ClarificationMetadata): CreateResultMetadata {
  const reviewReason = `Still ambiguous after clarification: ${clarification.ambiguityReason}`;
  return {
    resultTypeLabel: 'Needs Review',
    intentCategory: 'needs_review',
    requestedIntentCategory: clarification.resumeContext.requestedCategory,
    needsReview: true,
    needsClarification: false,
    reviewReason,
    saveDisabledReason: reviewReason,
  };
}

export function buildCreateClarificationResumePrompt(
  clarification: ClarificationMetadata,
  answer: CreateClarificationResumeAnswer,
): string {
  const answerText = answer.label && answer.label !== answer.value ? `${answer.label} (${answer.value})` : answer.value;
  const resolutionHint = getClarificationResolutionHint(clarification, answer);

  return [
    clarification.originalPrompt,
    '',
    `Clarification question: ${clarification.question}`,
    `Clarification answer: ${answerText}`,
    resolutionHint ? `Resolved routing: ${resolutionHint}` : undefined,
  ].filter((line): line is string => line !== undefined).join('\n');
}

export function getCreateClarificationSourceHint(
  clarification: ClarificationMetadata,
  option: ClarificationOption,
): string | undefined {
  return clarification.category === 'source_selection' ? option.value : clarification.resumeContext.sourceHint;
}

export function getCreateClarificationAmbiguityId(clarification: ClarificationMetadata): string {
  const optionSignature = clarification.options.map((option) => option.value).join('|');
  return `${clarification.category}:${clarification.type}:${optionSignature}`;
}

export function buildCreateClarificationResponsePayload(
  clarification: ClarificationMetadata,
  answer: CreateClarificationResumeAnswer,
): ClarificationResponsePayload {
  const normalizedAnswer: ClarificationAnswer = {
    value: answer.value,
    ...(answer.label && { label: answer.label }),
    ...(answer.optionId && { optionId: answer.optionId }),
    ...(answer.isFreeText !== undefined && { isFreeText: answer.isFreeText }),
  };
  const previousConsumedIds = clarification.resumeContext.consumedAmbiguityIds ?? [];
  const ambiguityId = getCreateClarificationAmbiguityId(clarification);

  return {
    originalPrompt: clarification.originalPrompt,
    clarification,
    answer: normalizedAnswer,
    depth: (clarification.resumeContext.depth ?? 0) + 1,
    consumedAmbiguityIds: Array.from(new Set([...previousConsumedIds, ambiguityId])),
  };
}

function getClarificationResolutionHint(
  clarification: ClarificationMetadata,
  answer: CreateClarificationResumeAnswer,
): string | null {
  const value = answer.value.trim();
  if (!value) {
    return null;
  }

  if (clarification.category === 'source_selection') {
    return `use selected source ${answer.label ?? value}`;
  }

  if (clarification.category === 'intent') {
    if (value === 'multi_metric_same_source') {
      return 'use one data source with multiple measures';
    }
    if (value === 'multi_source_combined') {
      return 'use separate data sources joined by time';
    }
  }

  if (clarification.category === 'time_range') {
    if (value === 'today_vs_yesterday') {
      return 'compare today vs yesterday';
    }
    if (value === 'current_24h_vs_previous_24h') {
      return 'compare current 24 hours vs previous 24 hours';
    }
    if (value === 'this_week_vs_last_week') {
      return 'compare this week vs last week';
    }
  }

  if (clarification.category === 'filter') {
    const entity = clarification.question.match(/which\s+([a-z0-9_-]+)/i)?.[1] ?? 'entity';
    return `use ${entity.toLowerCase()} ${answer.label ?? value}`;
  }

  return answer.label ?? value;
}

function getProjectedCompositeSourceCount(definition: CompositeQueryDefinition): number {
  const sourceIds = new Set(definition.sources.map((source) => source.id));
  return new Set(definition.fields.flatMap((field) => (sourceIds.has(field.sourceId) ? [field.sourceId] : []))).size;
}

function isUsableCompositeDefinition(definition: CompositeQueryDefinition | undefined): definition is CompositeQueryDefinition {
  if (!definition) {
    return false;
  }

  const sourceIds = new Set(definition.sources.map((source) => source.id));
  return (
    definition.sources.length >= 2 &&
    sourceIds.has(definition.primarySourceId) &&
    definition.joinKeys.length > 0 &&
    definition.fields.length > 0 &&
    getProjectedCompositeSourceCount(definition) >= 2
  );
}

function getCompositeInputSignature(source: CompositeQueryDefinition['sources'][number]): string {
  return JSON.stringify({
    schemaId: source.query.schemaId,
    filters: source.query.filters ?? {},
    timeRange: source.query.timeRange ?? {},
  });
}

function hasDistinctCompositeInputs(definition: CompositeQueryDefinition): boolean {
  return new Set(definition.sources.map(getCompositeInputSignature)).size > 1;
}

function textSuggestsPeriodComparison(text: string): boolean {
  const normalized = text.toLowerCase();
  const comparisonSignal = /\b(compare|comparing|comparison|versus|vs\.?|against|difference|delta|change|percent|percentage)\b/.test(normalized);
  const periodSignal =
    /\b(previous|prior|current|today|yesterday|this\s+(hour|day|week|month))\b/.test(normalized) ||
    /\blast\s+\d+\s*(hour|hours|day|days|week|weeks|month|months)\b.*\b(previous|prior|before|earlier)\b/.test(normalized);
  return comparisonSignal && periodSignal;
}

function textSuggestsOpenEndedPeriodComparison(text: string): boolean {
  const normalized = text.toLowerCase();
  const comparisonSignal = /\b(compare|comparing|comparison|difference|delta|change|percent|percentage)\b/.test(normalized);
  const usageSignal = /\b(water\s+usage|usage|consumption)\b/.test(normalized);
  return comparisonSignal && usageSignal && !textSuggestsPeriodComparison(normalized);
}

function textSuggestsDerivedMetric(text: string): boolean {
  const normalized = text.toLowerCase();
  return (
    /\b(ratio|rate|efficiency|utilization|derived|calculated|formula|index|score)\b/.test(normalized) ||
    /\b(difference|delta|change|percent|percentage)\b/.test(normalized) ||
    /\b(divided\s+by|minus|subtract(?:ed)?|net|normalized)\b/.test(normalized) ||
    /\b(?:total|sum)\s+(?:of|between|across)\b/.test(normalized) ||
    /\b(?:combined|added)\s+(?:total|sum)\b/.test(normalized)
  );
}

function textSuggestsNumberedEntityComparison(text: string): boolean {
  const normalized = text.toLowerCase();
  const comparisonSignal = /\b(compare|comparing|comparison|versus|vs\.?|against)\b/.test(normalized);
  if (!comparisonSignal) {
    return false;
  }

  return (
    /\b([a-z][a-z0-9_-]*)\s+\d+\s+(?:and|with|to|vs\.?|versus|against)\s+\1\s+\d+\b/.test(normalized) ||
    /\b(vs\.?|versus|against)\b/.test(normalized)
  );
}

function textSuggestsExplicitEntityComparison(text: string): boolean {
  const normalized = text.toLowerCase();
  if (!/\b(compare|comparing|comparison)\b/.test(normalized)) {
    return false;
  }

  return /\b(compare|comparing)\b.+\b(?:and|with|to|vs\.?|versus|against)\b.+/.test(normalized);
}

function textSuggestsExplicitDerivedComparison(text: string): boolean {
  const normalized = text.toLowerCase();
  if (!textSuggestsDerivedMetric(normalized)) {
    return false;
  }

  return (
    /\b(?:difference|delta|change|ratio|total|sum)\s+between\b.+\b(?:and|with|to|vs\.?|versus|against)\b/.test(normalized) ||
    /\b(?:percent|percentage)\s+change\b.+\b(?:and|with|to|vs\.?|versus|against)\b/.test(normalized)
  );
}

function textSegmentSuggestsSource(text: string): boolean {
  return /\b(source|schema|table|dataset|data\s+set|feed|stream|telemetry|device|sensor|meter|pump|tank|well|valve|controller|gateway|plc|asset|machine|equipment|line|production\s+line)\b/.test(
    text,
  );
}

function connectorSuggestsSeparateSources(text: string): boolean {
  const connectorPattern = /\b(with|plus|alongside|and)\b/g;
  let match = connectorPattern.exec(text);

  while (match) {
    const left = text.slice(0, match.index);
    const right = text.slice(match.index + match[0].length);
    if (textSegmentSuggestsSource(left) && textSegmentSuggestsSource(right)) {
      return true;
    }
    match = connectorPattern.exec(text);
  }

  return false;
}

function textSuggestsCombinedSources(text: string): boolean {
  const normalized = text.toLowerCase();
  const explicitCompositeSignal = /\b(combine|combined|join|joining|joined|merge|merged|correlate|correlation|relationship|overlay)\b/.test(normalized);
  return (
    explicitCompositeSignal ||
    connectorSuggestsSeparateSources(normalized) ||
    textSuggestsNumberedEntityComparison(normalized) ||
    textSuggestsExplicitEntityComparison(normalized) ||
    textSuggestsExplicitDerivedComparison(normalized)
  );
}

function textSuggestsCompositeIntent(text: string): boolean {
  return textSuggestsPeriodComparison(text) || textSuggestsOpenEndedPeriodComparison(text) || textSuggestsCombinedSources(text);
}

function definitionSuggestsPeriodComparison(definition: CompositeQueryDefinition): boolean {
  const labelText = [
    definition.sources.map((source) => `${source.id} ${source.label ?? ''}`).join(' '),
    definition.fields.map((field) => `${field.field} ${field.alias}`).join(' '),
    definition.computedFields?.map((field) => `${field.name} ${field.expression}`).join(' ') ?? '',
  ].join(' ');

  if (textSuggestsPeriodComparison(labelText)) {
    return true;
  }

  const timeRangeSignatures = new Set(
    definition.sources.flatMap((source) => (source.query.timeRange ? [JSON.stringify(source.query.timeRange)] : [])),
  );

  return definition.sources.length >= 2 && timeRangeSignatures.size >= 2;
}

function getCompositeShapeLabel(definition: CompositeQueryDefinition): CreateResultShapeLabel {
  return definitionSuggestsPeriodComparison(definition) ? 'Period Comparison' : 'Combined Sources';
}

function compositeDefinitionHasComputedFields(definition: CompositeQueryDefinition): boolean {
  return (definition.computedFields ?? []).length > 0;
}

function fieldAliasSuggestsDerivedMetric(alias: string): boolean {
  return textSuggestsDerivedMetric(alias.replace(/[_-]+/g, ' '));
}

function compositeDefinitionHasExplicitProjectedCalculation(definition: CompositeQueryDefinition): boolean {
  const sourceIdsWithPipeline = new Set(
    definition.sources
      .filter((source) => Array.isArray(source.query.aiGeneratedPipeline) && source.query.aiGeneratedPipeline.length > 0)
      .map((source) => source.id),
  );

  return definition.fields.some((field) => sourceIdsWithPipeline.has(field.sourceId) && fieldAliasSuggestsDerivedMetric(field.alias));
}

function compositeDefinitionHasDerivedOutput(definition: CompositeQueryDefinition): boolean {
  return compositeDefinitionHasComputedFields(definition) || compositeDefinitionHasExplicitProjectedCalculation(definition);
}

function getDerivedOutputAliases(definition: CompositeQueryDefinition): string[] {
  const aliases = new Set<string>();
  for (const computedField of definition.computedFields ?? []) {
    aliases.add(computedField.name);
  }

  const sourceIdsWithPipeline = new Set(
    definition.sources
      .filter((source) => Array.isArray(source.query.aiGeneratedPipeline) && source.query.aiGeneratedPipeline.length > 0)
      .map((source) => source.id),
  );

  for (const field of definition.fields) {
    if (sourceIdsWithPipeline.has(field.sourceId) && fieldAliasSuggestsDerivedMetric(field.alias)) {
      aliases.add(field.alias);
    }
  }

  return Array.from(aliases);
}

function templateQueryHasMultipleMeasures(response: QueryResponse): boolean {
  const measures = response.templateQuery?.measures ?? [];
  const uniqueMeasureKeys = new Set(measures.map((measure) => `${measure.aggregation}:${measure.field}:${measure.alias ?? ''}`));
  return uniqueMeasureKeys.size > 1;
}

function getMeasureSearchText(measure: NonNullable<QueryResponse['templateQuery']>['measures'][number]): string {
  return `${measure.field} ${measure.alias ?? ''}`
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ');
}

function measureSuggestsNumericMetric(measure: NonNullable<QueryResponse['templateQuery']>['measures'][number]): boolean {
  return NUMERIC_METRIC_TERM_PATTERN.test(getMeasureSearchText(measure));
}

function measureSuggestsCategoricalStatus(measure: NonNullable<QueryResponse['templateQuery']>['measures'][number]): boolean {
  return CATEGORICAL_STATUS_TERM_PATTERN.test(getMeasureSearchText(measure));
}

function templateQueryHasMetricAndStatus(response: QueryResponse | null): boolean {
  const measures = response?.templateQuery?.measures ?? [];
  return (
    measures.some(measureSuggestsNumericMetric) &&
    measures.some(measureSuggestsCategoricalStatus)
  );
}

function getPromptConnectorTermPairs(text: string): Array<{ left: string; right: string }> {
  const connectorPattern = /\b(?:and|plus|with|alongside)\b/g;
  const pairs: Array<{ left: string; right: string }> = [];
  let match = connectorPattern.exec(text);

  while (match) {
    const left = text.slice(0, match.index);
    const right = text.slice(match.index + match[0].length);
    pairs.push({
      left: getNearestPromptTerm(left, 'left'),
      right: getNearestPromptTerm(right, 'right'),
    });
    match = connectorPattern.exec(text);
  }

  return pairs;
}

function getNearestPromptTerm(text: string, side: 'left' | 'right'): string {
  const stopWords = new Set([
    'a',
    'an',
    'the',
    'me',
    'my',
    'our',
    'show',
    'display',
    'chart',
    'graph',
    'plot',
    'create',
    'build',
    'make',
    'template',
    'data',
    'metric',
    'metrics',
    'reading',
    'readings',
    'value',
    'values',
    'of',
    'to',
  ]);
  const words = text
    .toLowerCase()
    .match(/\b[a-z][a-z0-9_-]*\b/g)
    ?.filter((word) => !stopWords.has(word)) ?? [];
  const termWords = side === 'left' ? words.slice(-3) : words.slice(0, 3);

  return termWords.join(' ');
}

function isMeaningfulPromptTerm(term: string): boolean {
  return /\b[a-z][a-z0-9_-]*\b/.test(term);
}

function promptMentionsMultipleMetrics(text: string): boolean {
  const normalized = text.toLowerCase();
  return getPromptConnectorTermPairs(normalized).some(
    (pair) => isMeaningfulPromptTerm(pair.left) && isMeaningfulPromptTerm(pair.right),
  );
}

function promptSuggestsMetricWithCategoricalStatus(text: string): boolean {
  const normalized = text.toLowerCase();
  const connectorPattern = /\b(?:and|plus|with|alongside)\b/g;
  let match = connectorPattern.exec(normalized);

  while (match) {
    const left = normalized.slice(0, match.index);
    const right = normalized.slice(match.index + match[0].length);
    if (
      (NUMERIC_METRIC_TERM_PATTERN.test(left) && CATEGORICAL_STATUS_TERM_PATTERN.test(right)) ||
      (CATEGORICAL_STATUS_TERM_PATTERN.test(left) && NUMERIC_METRIC_TERM_PATTERN.test(right))
    ) {
      return true;
    }
    match = connectorPattern.exec(normalized);
  }

  return false;
}

function promptSuggestsCategoricalStatusWithoutMetric(text: string): boolean {
  return CATEGORICAL_STATUS_TERM_PATTERN.test(text) && !NUMERIC_METRIC_TERM_PATTERN.test(text);
}

function promptHasDisambiguatingRoutingQualifier(text: string): boolean {
  return /\b(by|per|grouped\s+by|broken\s+down\s+by|bucketed\s+by|from|for|where|over|last|today|yesterday|current|previous|prior|past|next|this|since|until|before|after|between)\b/.test(
    text,
  );
}

function promptSuggestsAmbiguousMetricRouting(text: string): boolean {
  const normalized = text.toLowerCase();
  return promptMentionsMultipleMetrics(normalized) && !promptHasDisambiguatingRoutingQualifier(normalized);
}

function promptSuggestsMultipleMetrics(text: string): boolean {
  const normalized = text.toLowerCase();
  if (!/\b(by|over|per|grouped\s+by)\b/.test(normalized)) {
    return false;
  }

  return promptMentionsMultipleMetrics(normalized) && !textSuggestsCombinedSources(normalized);
}

function getBackendRequestedIntentCategory(response?: QueryResponse): CreateResultRequestedIntentCategory | null {
  const category = response?.templateIntent?.requestedCategory ?? response?.templateIntent?.category;
  if (!category || category === 'needs_clarification' || category === 'needs_review') {
    return null;
  }

  return category;
}

function getRequestedIntentCategory(prompt: string, response?: QueryResponse): CreateResultRequestedIntentCategory {
  const backendCategory = getBackendRequestedIntentCategory(response);
  if (backendCategory) {
    return backendCategory;
  }

  if (textSuggestsPeriodComparison(prompt) || textSuggestsOpenEndedPeriodComparison(prompt)) {
    return 'period_comparison';
  }

  if (textSuggestsDerivedMetric(prompt)) {
    return 'derived_metric';
  }

  if (promptSuggestsMetricWithCategoricalStatus(prompt) && templateQueryHasMetricAndStatus(response ?? null)) {
    return 'multi_metric_same_source';
  }

  if (textSuggestsCombinedSources(prompt)) {
    return 'multi_source_combined';
  }

  if ((response && templateQueryHasMultipleMeasures(response)) || promptSuggestsMultipleMetrics(prompt)) {
    return 'multi_metric_same_source';
  }

  return 'single_metric_over_time';
}

function getCompositeIntentCategory(definition: CompositeQueryDefinition, prompt: string): CreateResultRequestedIntentCategory {
  if (textSuggestsPeriodComparison(prompt) || textSuggestsOpenEndedPeriodComparison(prompt) || definitionSuggestsPeriodComparison(definition)) {
    return 'period_comparison';
  }

  if (textSuggestsDerivedMetric(prompt) || compositeDefinitionHasComputedFields(definition)) {
    return 'derived_metric';
  }

  return 'multi_source_combined';
}

function normalizeCompositeOutputField(field: string): string {
  return field.trim().replace(/\./g, '_');
}

function getSourceTimeGroupByFields(source: CompositeQueryDefinition['sources'][number]): Map<string, TimeGranularity> {
  const fields = new Map<string, TimeGranularity>();

  for (const groupBy of source.query.groupBy ?? []) {
    if (!groupBy.granularity) {
      continue;
    }

    fields.set(normalizeCompositeOutputField(groupBy.field), groupBy.granularity);
  }

  return fields;
}

function resolvePeriodComparisonJoinGranularity(
  definition: CompositeQueryDefinition,
  joinKey: CompositeJoinKey,
): TimeGranularity | null {
  const primarySource = definition.sources.find((source) => source.id === definition.primarySourceId);
  const secondarySource = definition.sources.find((source) => source.id !== definition.primarySourceId);
  if (!primarySource || !secondarySource) {
    return null;
  }

  const primaryTimeFields = getSourceTimeGroupByFields(primarySource);
  const secondaryTimeFields = getSourceTimeGroupByFields(secondarySource);
  const leftGranularity = primaryTimeFields.get(normalizeCompositeOutputField(joinKey.left));
  const rightGranularity = secondaryTimeFields.get(normalizeCompositeOutputField(joinKey.right));

  return leftGranularity && leftGranularity === rightGranularity ? leftGranularity : null;
}

export function normalizePeriodComparisonAlignment(
  definition: CompositeQueryDefinition,
  prompt: string,
): CompositeQueryDefinition {
  const requestedPeriodComparison =
    textSuggestsPeriodComparison(prompt) ||
    textSuggestsOpenEndedPeriodComparison(prompt) ||
    definitionSuggestsPeriodComparison(definition);

  if (!requestedPeriodComparison) {
    return definition;
  }

  let changed = false;
  const joinKeys = definition.joinKeys.map((joinKey) => {
    const granularity = resolvePeriodComparisonJoinGranularity(definition, joinKey);
    if (!granularity) {
      return joinKey;
    }

    if (joinKey.kind === 'time-bucket' && joinKey.granularity === granularity) {
      return joinKey;
    }

    changed = true;
    return {
      left: joinKey.left,
      right: joinKey.right,
      kind: 'time-bucket' as const,
      granularity,
    };
  });

  return changed ? { ...definition, joinKeys } : definition;
}

function isMeaningfulPreviewValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

function getCompositePreviewIssue(
  definition: CompositeQueryDefinition,
  previewRows: Record<string, unknown>[],
  options: { requireDerivedOutput?: boolean } = {},
): string | null {
  if (previewRows.length === 0) {
    return 'The generated multi-source template returned no preview rows. Review the source filters, time range, and join settings before saving.';
  }

  if (options.requireDerivedOutput) {
    const derivedAliases = getDerivedOutputAliases(definition);
    if (
      derivedAliases.length > 0 &&
      !derivedAliases.some((alias) => previewRows.some((row) => isMeaningfulPreviewValue(row[alias])))
    ) {
      return 'The prompt asks for a derived metric, but the preview does not include a computed derived value. Review or rebuild it before saving.';
    }
  }

  const projectedAliasesBySource = new Map<string, string[]>();
  for (const source of definition.sources) {
    projectedAliasesBySource.set(source.id, []);
  }

  for (const field of definition.fields) {
    const aliases = projectedAliasesBySource.get(field.sourceId);
    if (aliases) {
      aliases.push(field.alias);
    }
  }

  for (const computedField of definition.computedFields ?? []) {
    projectedAliasesBySource.get(definition.primarySourceId)?.push(computedField.name);
  }

  const sourceIdsWithProjectedValues = definition.sources
    .map((source) => source.id)
    .filter((sourceId) => {
      const aliases = projectedAliasesBySource.get(sourceId) ?? [];
      return aliases.some((alias) => previewRows.some((row) => isMeaningfulPreviewValue(row[alias])));
    });

  if (sourceIdsWithProjectedValues.length < 2) {
    return 'The preview does not include values from both generated sources. Review the source filters and join keys before saving.';
  }

  const hasJoinedRow = previewRows.some((row) => {
    let sourcesWithValues = 0;
    for (const aliases of projectedAliasesBySource.values()) {
      if (aliases.some((alias) => isMeaningfulPreviewValue(row[alias]))) {
        sourcesWithValues += 1;
      }
    }
    return sourcesWithValues >= 2;
  });

  if (!hasJoinedRow) {
    return 'The preview only contains unmatched rows from separate sources. Review the join type, join keys, and time bucket before saving.';
  }

  return null;
}

function getTopMatchedSourceConfidence(response: QueryResponse | null): number | null {
  const confidence = response?.debug?.matchedSources?.[0]?.confidence;
  return typeof confidence === 'number' ? confidence : null;
}

function singleResultHasAmbiguousSourceMatch(response: QueryResponse | null): boolean {
  const matchedSources = response?.debug?.matchedSources ?? [];
  const topSource = matchedSources[0];
  const secondSource = matchedSources[1];

  if (!topSource || !secondSource || secondSource.confidence < 0.55) {
    return false;
  }

  return topSource.confidence - secondSource.confidence <= 0.15;
}

function singleTemplateSupportsPeriodComparison(_response: QueryResponse): boolean {
  return false;
}

function singleResultNeedsCompositeFallback(response: QueryResponse | null): boolean {
  if (responseNeedsClarification(response)) {
    return false;
  }

  if (!hasTemplateQuery(response) || hasQueryResponseError(response)) {
    return true;
  }

  const topConfidence = getTopMatchedSourceConfidence(response);
  if (topConfidence !== null && topConfidence < 0.55) {
    return true;
  }

  return singleResultHasAmbiguousSourceMatch(response);
}

function responseTextSuggestsTemplateCannotBeGenerated(response: QueryResponse): boolean {
  const text = [response.answer, response.error, response.suggestion]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();

  return (
    /\b(template|query)\s+cannot\s+be\s+generated\b/.test(text) ||
    /\bcannot\s+generate\s+(?:a\s+)?(?:template|query)\b/.test(text) ||
    /\bno\s+[^.]{0,80}\s+data\s+(?:appears|exists|found|available)\b/.test(text) ||
    /\bdoes\s+not\s+appear\s+to\s+exist\b/.test(text)
  );
}

function normalizeGeneratedFieldName(value: string | null | undefined): string {
  return value?.replace(/[^a-z0-9]/gi, '').toLowerCase() ?? '';
}

type AccessibleSchemaField = NonNullable<AccessibleSchema['fields']>[number];
type SelectedDebugField = NonNullable<NonNullable<QueryResponse['debug']>['selectedFields']>['fields'][number];
type EvidenceFieldInput = AccessibleSchemaField | SelectedDebugField;

interface MetricStatusPromptTerms {
  metricTerm: string;
  statusTerm: string;
}

interface MetricStatusPromptTimeRange {
  preset: string;
  granularity: TimeGranularity;
}

interface MetricStatusEvidenceField {
  name: string;
  path: string;
  type: string | null;
  description: string | null;
}

interface MetricStatusEvidenceSource {
  schemaId: string;
  schemaName: string;
  groupId: string;
  fields: MetricStatusEvidenceField[];
}

interface MetricStatusFieldMatch {
  source: MetricStatusEvidenceSource;
  field: MetricStatusEvidenceField;
}

interface MetricStatusEvidenceMatch {
  metric: MetricStatusFieldMatch;
  status: MetricStatusFieldMatch;
  joinField: string | null;
}

type MetricStatusSynthesisReason =
  | 'synthesized'
  | 'synthesis_not_attempted'
  | 'no_metric_status_evidence'
  | 'no_shared_join_key_time'
  | 'composite_unusable';

type MetricStatusCompositeState =
  | 'not_attempted'
  | 'failed'
  | 'missing_definition'
  | 'unusable_definition'
  | 'usable_not_selected';

type MetricStatusEvidenceState =
  | 'not_checked'
  | 'none'
  | 'metric_only'
  | 'status_only'
  | 'separate_without_join'
  | 'complete_same_source'
  | 'complete_joined';

export interface MetricStatusSynthesisDiagnostic {
  attempted: boolean;
  reason: MetricStatusSynthesisReason;
  compositeState: MetricStatusCompositeState;
  evidenceState: MetricStatusEvidenceState;
  message: string;
}

export interface MetricStatusCreateResponseLogSummary {
  responseStatus: {
    success: boolean;
    errorPresent: boolean;
    errorCodePresent: boolean;
    needsSourceSelection: boolean;
    clarificationPresent: boolean;
    templateIntentCategory: string | null;
  };
  topLevelResponseKeys: string[];
  templateQuery: {
    measures: Array<{
      field: string;
      alias: string | null;
      aggregation: string;
    }>;
    groupByFields: string[];
    timeRangeField: string | null;
    timeRangePreset: string | null;
    orderByFields: string[];
  };
  sampleRow: {
    rowCount: number;
    keys: string[];
    metricAliasHasValue: boolean;
    statusAliasHasValue: boolean;
    matchedMetricFieldHasValue: boolean;
    matchedStatusFieldHasValue: boolean;
  };
  metricStatusSynthesis: MetricStatusSynthesisDiagnostic | null;
  matchedFields: {
    metric: string[];
    status: string[];
    metricWithSampleValue: string[];
    statusWithSampleValue: string[];
  };
  evidence: {
    debugPresent: boolean;
    schemaContextPresent: boolean;
    accessibleSchemaCount: number;
    matchedSourceCount: number;
    selectedFieldCount: number;
    sampleEvidencePresent: boolean;
    generatedQueryPresent: boolean;
    validationResultPresent: boolean;
  };
}

interface MetricStatusEvidenceDiagnosis {
  match: MetricStatusEvidenceMatch | null;
  metricMatches: MetricStatusFieldMatch[];
  statusMatches: MetricStatusFieldMatch[];
  evidenceState: MetricStatusEvidenceState;
}

interface MetricStatusFallbackAttempt {
  result: CreateTemplateResult | null;
  diagnostic: MetricStatusSynthesisDiagnostic;
}

interface MetricStatusFallbackAttemptOptions {
  allowIncompleteSingleSourceFallback?: boolean;
  compositeState?: MetricStatusCompositeState;
}

function getMetricStatusPromptTerms(prompt: string): MetricStatusPromptTerms | null {
  const pairs = getPromptConnectorTermPairs(prompt.toLowerCase());

  for (const pair of pairs) {
    const leftHasMetric = NUMERIC_METRIC_TERM_PATTERN.test(pair.left);
    const rightHasMetric = NUMERIC_METRIC_TERM_PATTERN.test(pair.right);
    const leftHasStatus = CATEGORICAL_STATUS_TERM_PATTERN.test(pair.left);
    const rightHasStatus = CATEGORICAL_STATUS_TERM_PATTERN.test(pair.right);

    if (leftHasMetric && rightHasStatus && isMeaningfulPromptTerm(pair.left) && isMeaningfulPromptTerm(pair.right)) {
      return {
        metricTerm: pair.left,
        statusTerm: pair.right,
      };
    }

    if (leftHasStatus && rightHasMetric && isMeaningfulPromptTerm(pair.left) && isMeaningfulPromptTerm(pair.right)) {
      return {
        metricTerm: pair.right,
        statusTerm: pair.left,
      };
    }
  }

  return null;
}

function getMetricStatusPromptTimeRange(prompt: string): MetricStatusPromptTimeRange | null {
  const normalized = prompt.toLowerCase();

  if (/\b(last|past)\s+24\s*(hours?|hrs?)\b|\b24h\b/.test(normalized)) {
    return { preset: 'last_24h', granularity: 'hour' };
  }

  if (/\btoday\b/.test(normalized)) {
    return { preset: 'today', granularity: 'hour' };
  }

  if (/\byesterday\b/.test(normalized)) {
    return { preset: 'yesterday', granularity: 'hour' };
  }

  if (/\b(last|past)\s+7\s*days?\b|\blast\s+week\b/.test(normalized)) {
    return { preset: 'last_7d', granularity: 'day' };
  }

  return null;
}

function getSchemaFieldName(field: EvidenceFieldInput): string | null {
  return typeof field === 'string' ? field : field.name;
}

function getSchemaFieldPath(field: EvidenceFieldInput): string | null {
  if (typeof field === 'string') {
    return field;
  }

  return field.path ?? field.name;
}

function getSchemaFieldType(field: EvidenceFieldInput): string | null {
  if (typeof field === 'string') {
    return null;
  }

  return 'type' in field ? field.type ?? field.dataType ?? field.technicalType ?? null : null;
}

function getSchemaFieldDescription(field: EvidenceFieldInput): string | null {
  return typeof field === 'string' || !('description' in field) ? null : field.description ?? null;
}

function normalizeEvidenceField(field: EvidenceFieldInput): MetricStatusEvidenceField | null {
  const name = getSchemaFieldName(field)?.trim();
  const path = getSchemaFieldPath(field)?.trim();
  if (!name || !path) {
    return null;
  }

  return {
    name,
    path,
    type: getSchemaFieldType(field),
    description: getSchemaFieldDescription(field),
  };
}

function createInferredEvidenceField(name: string, value: unknown, prefix?: string): MetricStatusEvidenceField | null {
  const trimmedName = name.trim();
  if (!trimmedName || FORBIDDEN_DYNAMIC_KEYS.has(trimmedName)) {
    return null;
  }

  const path = prefix ? `${prefix}.${trimmedName}` : trimmedName;
  const inferredType =
    typeof value === 'number'
      ? 'number'
      : typeof value === 'boolean'
        ? 'boolean'
        : typeof value === 'string'
          ? 'string'
          : null;

  return {
    name: trimmedName,
    path,
    type: inferredType,
    description: null,
  };
}

function schemaMatchesSource(
  schema: AccessibleSchema,
  matchedSource: NonNullable<QueryResponse['debug']>['matchedSources'][number],
): boolean {
  return (
    schema.id === matchedSource.schemaId ||
    schema.name === matchedSource.schemaName ||
    schema.displayName === matchedSource.schemaName
  );
}

function mergeEvidenceFields(fields: MetricStatusEvidenceField[]): MetricStatusEvidenceField[] {
  const fieldsByPath = new Map<string, MetricStatusEvidenceField>();

  for (const field of fields) {
    const key = normalizeGeneratedFieldName(field.path);
    if (!key) {
      continue;
    }

    const existing = fieldsByPath.get(key);
    if (!existing) {
      fieldsByPath.set(key, field);
      continue;
    }

    fieldsByPath.set(key, {
      ...existing,
      type: existing.type ?? field.type,
      description: existing.description ?? field.description,
    });
  }

  return Array.from(fieldsByPath.values());
}

function mergeEvidenceSource(
  sourcesByKey: Map<string, MetricStatusEvidenceSource>,
  source: MetricStatusEvidenceSource,
): void {
  const key = `${source.schemaId}:${source.groupId}`;
  const existing = sourcesByKey.get(key);
  if (!existing) {
    sourcesByKey.set(key, {
      ...source,
      fields: mergeEvidenceFields(source.fields),
    });
    return;
  }

  sourcesByKey.set(key, {
    ...existing,
    schemaName: existing.schemaName || source.schemaName,
    fields: mergeEvidenceFields([...existing.fields, ...source.fields]),
  });
}

function getPrimaryEvidenceSourceIdentity(response: QueryResponse): Omit<MetricStatusEvidenceSource, 'fields'> | null {
  const matchedSource = response.debug?.matchedSources?.[0];
  if (matchedSource) {
    return {
      schemaId: matchedSource.schemaId,
      schemaName: matchedSource.schemaName,
      groupId: matchedSource.groupId,
    };
  }

  if (response.source) {
    return {
      schemaId: response.source.id,
      schemaName: response.source.name,
      groupId: response.templateQuery?.groupId ?? response.source.id,
    };
  }

  return null;
}

function getSelectedFieldEvidence(response: QueryResponse): MetricStatusEvidenceField[] {
  return (response.debug?.selectedFields?.fields ?? []).flatMap((field) => {
    const normalized = normalizeEvidenceField(field);
    return normalized ? [normalized] : [];
  });
}

function getSampleRowEvidenceFields(response: QueryResponse): MetricStatusEvidenceField[] {
  const fields: MetricStatusEvidenceField[] = [];

  for (const row of response.data ?? []) {
    if (!isRecord(row)) {
      continue;
    }

    for (const [key, value] of Object.entries(row)) {
      if (FORBIDDEN_DYNAMIC_KEYS.has(key)) {
        continue;
      }

      if ((isGenericRecordFieldName(key) || key === 'data' || key === 'metadata') && isRecord(value)) {
        const prefix = key === 'data' || key === 'metadata' ? key : undefined;
        for (const [nestedKey, nestedValue] of Object.entries(value)) {
          const nestedField = createInferredEvidenceField(nestedKey, nestedValue, prefix);
          if (nestedField) {
            fields.push(nestedField);
          }
        }
        continue;
      }

      const field = createInferredEvidenceField(key, value);
      if (field) {
        fields.push(field);
      }
    }
  }

  return mergeEvidenceFields(fields);
}

function getMetricStatusEvidenceSources(response: QueryResponse): MetricStatusEvidenceSource[] {
  const schemas = response.debug?.schemaContext?.accessibleSchemas ?? [];
  const matchedSources = response.debug?.matchedSources ?? [];
  const sourcesByKey = new Map<string, MetricStatusEvidenceSource>();

  for (const schema of schemas) {
    const matchedSource = matchedSources.find((source) => schemaMatchesSource(schema, source));
    const fields = (schema.fields ?? []).flatMap((field) => {
      const normalized = normalizeEvidenceField(field);
      return normalized ? [normalized] : [];
    });

    if (fields.length === 0) {
      continue;
    }

    mergeEvidenceSource(sourcesByKey, {
      schemaId: matchedSource?.schemaId ?? schema.id,
      schemaName: matchedSource?.schemaName ?? schema.displayName ?? schema.name,
      groupId: matchedSource?.groupId ?? schema.groupId,
      fields,
    });
  }

  const primarySource = getPrimaryEvidenceSourceIdentity(response);
  if (primarySource) {
    const fallbackFields = mergeEvidenceFields([
      ...getSelectedFieldEvidence(response),
      ...getSampleRowEvidenceFields(response),
    ]);
    if (fallbackFields.length > 0) {
      mergeEvidenceSource(sourcesByKey, {
        ...primarySource,
        fields: fallbackFields,
      });
    }
  }

  return Array.from(sourcesByKey.values());
}

function tokenizeForFieldMatch(value: string): string[] {
  const expanded = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  return expanded.toLowerCase().match(/[a-z0-9]+/g) ?? [];
}

function meaningfulFieldTokens(value: string): string[] {
  const stopWords = new Set([
    'a',
    'an',
    'and',
    'by',
    'data',
    'field',
    'for',
    'in',
    'latest',
    'last',
    'metric',
    'of',
    'over',
    'reading',
    'readings',
    'the',
    'value',
    'values',
    'with',
  ]);

  return tokenizeForFieldMatch(value).filter((token) => !stopWords.has(token));
}

function fieldSearchText(source: MetricStatusEvidenceSource, field: MetricStatusEvidenceField): string {
  return [source.schemaName, source.schemaId, field.name, field.path, field.description ?? '']
    .join(' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ');
}

function evidenceFieldSearchText(field: MetricStatusEvidenceField): string {
  return [field.name, field.path, field.description ?? '']
    .join(' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ');
}

function fieldMatchesPromptTerm(source: MetricStatusEvidenceSource, field: MetricStatusEvidenceField, term: string): boolean {
  const promptTokens = meaningfulFieldTokens(term);
  const fieldTokens = new Set(meaningfulFieldTokens(fieldSearchText(source, field)));
  return promptTokens.length > 0 && promptTokens.every((token) => fieldTokens.has(token));
}

function evidenceFieldHasAnyToken(field: MetricStatusEvidenceField, tokens: string[]): boolean {
  const fieldTokens = new Set(meaningfulFieldTokens(evidenceFieldSearchText(field)));
  return tokens.some((token) => fieldTokens.has(token));
}

function fieldLooksLikeTankLevelConcept(_source: MetricStatusEvidenceSource, field: MetricStatusEvidenceField): boolean {
  const normalizedFieldName = normalizeGeneratedFieldName(field.name);
  const normalizedFieldPath = normalizeGeneratedFieldName(field.path);
  const text = evidenceFieldSearchText(field).toLowerCase();
  return (
    normalizedFieldName === 'tanklevel' ||
    normalizedFieldPath === 'tanklevel' ||
    normalizedFieldName === 'percentfull' ||
    normalizedFieldPath === 'percentfull' ||
    /\bpercent\s+full\b/.test(text) ||
    /\bpercentage\s+full\b/.test(text) ||
    evidenceFieldHasAnyToken(field, ['level'])
  );
}

function fieldLooksLikeStatusConcept(_source: MetricStatusEvidenceSource, field: MetricStatusEvidenceField): boolean {
  return (
    evidenceFieldHasAnyToken(field, ['status', 'state', 'mode', 'condition', 'enabled', 'active', 'running', 'open', 'closed', 'valve']) ||
    CATEGORICAL_STATUS_TERM_PATTERN.test(evidenceFieldSearchText(field))
  );
}

function metricFieldMatchesPromptTerm(
  source: MetricStatusEvidenceSource,
  field: MetricStatusEvidenceField,
  term: string,
): boolean {
  if (fieldMatchesPromptTerm(source, field, term)) {
    return true;
  }

  const promptTokens = new Set(meaningfulFieldTokens(term));
  return promptTokens.has('level') && fieldLooksLikeTankLevelConcept(source, field);
}

function statusFieldMatchesPromptTerm(
  source: MetricStatusEvidenceSource,
  field: MetricStatusEvidenceField,
  term: string,
): boolean {
  if (fieldMatchesPromptTerm(source, field, term)) {
    return true;
  }

  const promptTokens = new Set(meaningfulFieldTokens(term));
  const promptWantsStatusConcept = ['status', 'state', 'valve', 'enabled', 'open', 'closed'].some((token) =>
    promptTokens.has(token),
  );
  return promptWantsStatusConcept && fieldLooksLikeStatusConcept(source, field);
}

function isNumericEvidenceField(_source: MetricStatusEvidenceSource, field: MetricStatusEvidenceField): boolean {
  const type = field.type?.toLowerCase();
  if (type) {
    return /\b(number|numeric|integer|int|long|float|double|decimal)\b/.test(type);
  }

  return NUMERIC_METRIC_TERM_PATTERN.test(evidenceFieldSearchText(field));
}

function isCategoricalEvidenceField(_source: MetricStatusEvidenceSource, field: MetricStatusEvidenceField): boolean {
  const type = field.type?.toLowerCase();
  if (type && /\b(number|numeric|integer|int|long|float|double|decimal|date|datetime|timestamp)\b/.test(type)) {
    return false;
  }

  return CATEGORICAL_STATUS_TERM_PATTERN.test(evidenceFieldSearchText(field));
}

function isTimeEvidenceField(field: MetricStatusEvidenceField): boolean {
  const type = field.type?.toLowerCase();
  return (
    Boolean(type && /\b(date|datetime|timestamp|time)\b/.test(type)) ||
    /\b(timestamp|datetime|date|time)\b/i.test(field.name.replace(/[_-]+/g, ' '))
  );
}

function isStableKeyEvidenceField(field: MetricStatusEvidenceField): boolean {
  return /\b(device|asset|machine|equipment|sensor|meter|line)?\s*id\b/i.test(field.name.replace(/[_-]+/g, ' '));
}

function addSampleRowKeyName(keyNames: Set<string>, key: string, prefix?: string): void {
  const trimmedKey = key.trim();
  if (!trimmedKey || FORBIDDEN_DYNAMIC_KEYS.has(trimmedKey)) {
    return;
  }

  keyNames.add(prefix ? `${prefix}.${trimmedKey}` : trimmedKey);
}

function getSampleRowKeyNames(response: QueryResponse): string[] {
  const keyNames = new Set<string>();

  for (const row of response.data ?? []) {
    if (!isRecord(row)) {
      continue;
    }

    for (const [key, value] of Object.entries(row)) {
      addSampleRowKeyName(keyNames, key);

      if ((isGenericRecordFieldName(key) || key === 'data' || key === 'metadata') && isRecord(value)) {
        const prefix = key === 'data' || key === 'metadata' ? key : undefined;
        for (const nestedKey of Object.keys(value)) {
          addSampleRowKeyName(keyNames, nestedKey, prefix);
        }
      }
    }
  }

  return Array.from(keyNames).slice(0, METRIC_STATUS_DEBUG_SAMPLE_KEY_LIMIT);
}

function sanitizeMetricStatusLogName(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (!trimmed || FORBIDDEN_DYNAMIC_KEYS.has(trimmed)) {
    return null;
  }

  return trimmed.replace(RAW_OBJECT_ID_PATTERN, '[redacted-id]');
}

function compactMetricStatusLogNames(values: Array<string | null | undefined>): string[] {
  const names = new Set<string>();

  for (const value of values) {
    const sanitized = sanitizeMetricStatusLogName(value);
    if (sanitized) {
      names.add(sanitized);
    }
  }

  return Array.from(names);
}

function getGroupByFieldName(groupBy: NonNullable<NonNullable<QueryResponse['templateQuery']>['groupBy']>[number]): string {
  return typeof groupBy === 'string' ? groupBy : groupBy.field;
}

function responseRowsContainValueForAlias(response: QueryResponse, alias: string): boolean {
  return (response.data ?? []).some((row) => isRecord(row) && isMeaningfulPreviewValue(row[alias]));
}

function fieldMatchesWithSampleValue(
  response: QueryResponse,
  matches: MetricStatusFieldMatch[],
): MetricStatusFieldMatch[] {
  return matches.filter((match) => isMeaningfulPreviewValue(findSampleValueForField(response, match.field)));
}

function getMetricStatusLogEvidenceDiagnosis(response: QueryResponse, prompt: string): MetricStatusEvidenceDiagnosis {
  const terms = getMetricStatusPromptTerms(prompt) ?? { metricTerm: '', statusTerm: '' };
  return diagnoseMetricStatusEvidenceMatch(getMetricStatusEvidenceSources(response), terms);
}

export function buildMetricStatusCreateResponseLogSummary(
  prompt: string,
  response: QueryResponse,
  diagnostic?: MetricStatusSynthesisDiagnostic,
): MetricStatusCreateResponseLogSummary | null {
  if (!promptSuggestsMetricWithCategoricalStatus(prompt)) {
    return null;
  }

  const evidenceDiagnosis = getMetricStatusLogEvidenceDiagnosis(response, prompt);
  const measures = response.templateQuery?.measures ?? [];
  const metricAliasNames = measures
    .filter(measureSuggestsNumericMetric)
    .flatMap((measure) => measure.alias ?? measure.field);
  const statusAliasNames = measures
    .filter(measureSuggestsCategoricalStatus)
    .flatMap((measure) => measure.alias ?? measure.field);
  const sampleRowKeys = compactMetricStatusLogNames(getSampleRowKeyNames(response));
  const responseData = response.data ?? [];

  return {
    responseStatus: {
      success: response.success,
      errorPresent: typeof response.error === 'string' && response.error.length > 0,
      errorCodePresent: typeof response.errorCode === 'string' && response.errorCode.length > 0,
      needsSourceSelection: response.needsSourceSelection === true,
      clarificationPresent: Boolean(response.clarification),
      templateIntentCategory: sanitizeMetricStatusLogName(response.templateIntent?.category) ?? null,
    },
    topLevelResponseKeys: compactMetricStatusLogNames(Object.keys(response)),
    templateQuery: {
      measures: measures.map((measure) => ({
        field: sanitizeMetricStatusLogName(measure.field) ?? '[redacted-field]',
        alias: sanitizeMetricStatusLogName(measure.alias) ?? null,
        aggregation: measure.aggregation,
      })),
      groupByFields: compactMetricStatusLogNames((response.templateQuery?.groupBy ?? []).map(getGroupByFieldName)),
      timeRangeField: sanitizeMetricStatusLogName(response.templateQuery?.timeRange?.field) ?? null,
      timeRangePreset: sanitizeMetricStatusLogName(response.templateQuery?.timeRange?.preset) ?? null,
      orderByFields: compactMetricStatusLogNames((response.templateQuery?.orderBy ?? []).map((order) => order.field)),
    },
    sampleRow: {
      rowCount: responseData.length,
      keys: sampleRowKeys,
      metricAliasHasValue: metricAliasNames.some((alias) => responseRowsContainValueForAlias(response, alias)),
      statusAliasHasValue: statusAliasNames.some((alias) => responseRowsContainValueForAlias(response, alias)),
      matchedMetricFieldHasValue: fieldMatchesWithSampleValue(response, evidenceDiagnosis.metricMatches).length > 0,
      matchedStatusFieldHasValue: fieldMatchesWithSampleValue(response, evidenceDiagnosis.statusMatches).length > 0,
    },
    metricStatusSynthesis: diagnostic ?? null,
    matchedFields: {
      metric: compactMetricStatusLogNames(evidenceDiagnosis.metricMatches.map((match) => match.field.name)),
      status: compactMetricStatusLogNames(evidenceDiagnosis.statusMatches.map((match) => match.field.name)),
      metricWithSampleValue: compactMetricStatusLogNames(
        fieldMatchesWithSampleValue(response, evidenceDiagnosis.metricMatches).map((match) => match.field.name),
      ),
      statusWithSampleValue: compactMetricStatusLogNames(
        fieldMatchesWithSampleValue(response, evidenceDiagnosis.statusMatches).map((match) => match.field.name),
      ),
    },
    evidence: {
      debugPresent: Boolean(response.debug),
      schemaContextPresent: Boolean(response.debug?.schemaContext),
      accessibleSchemaCount: response.debug?.schemaContext?.accessibleSchemas.length ?? 0,
      matchedSourceCount: response.debug?.matchedSources.length ?? 0,
      selectedFieldCount: response.debug?.selectedFields?.fields.length ?? 0,
      sampleEvidencePresent: sampleRowKeys.length > 0,
      generatedQueryPresent: response.debug?.generatedQuery !== undefined,
      validationResultPresent: response.debug?.validationResult !== undefined,
    },
  };
}

function logMetricStatusCreateResponseDiagnostic(
  prompt: string,
  response: QueryResponse,
  diagnostic?: MetricStatusSynthesisDiagnostic,
): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const summary = buildMetricStatusCreateResponseLogSummary(prompt, response, diagnostic);
  if (!summary) {
    return;
  }

  console.debug(METRIC_STATUS_CREATE_LOG_PREFIX, summary);
}

function findField(
  source: MetricStatusEvidenceSource,
  term: string,
  predicate: (source: MetricStatusEvidenceSource, field: MetricStatusEvidenceField) => boolean,
  termMatcher: (source: MetricStatusEvidenceSource, field: MetricStatusEvidenceField, term: string) => boolean,
): MetricStatusEvidenceField | null {
  return source.fields.find((field) => predicate(source, field) && termMatcher(source, field, term)) ?? null;
}

function diagnoseMetricStatusEvidenceMatch(
  sources: MetricStatusEvidenceSource[],
  terms: MetricStatusPromptTerms,
): MetricStatusEvidenceDiagnosis {
  const metricMatches = sources.flatMap((source) => {
    const field = findField(source, terms.metricTerm, isNumericEvidenceField, metricFieldMatchesPromptTerm);
    return field ? [{ source, field }] : [];
  });
  const statusMatches = sources.flatMap((source) => {
    const field = findField(source, terms.statusTerm, isCategoricalEvidenceField, statusFieldMatchesPromptTerm);
    return field ? [{ source, field }] : [];
  });

  for (const source of sources) {
    const metric = metricMatches.find((match) => match.source === source);
    const status = statusMatches.find((match) => match.source === source);
    if (metric && status) {
      return {
        match: {
          metric,
          status,
          joinField: null,
        },
        metricMatches,
        statusMatches,
        evidenceState: 'complete_same_source',
      };
    }
  }

  for (const metric of metricMatches) {
    for (const status of statusMatches) {
      if (metric.source.schemaId === status.source.schemaId) {
        continue;
      }

      const joinField = findSharedJoinField(metric.source, status.source);
      if (joinField) {
        return {
          match: { metric, status, joinField },
          metricMatches,
          statusMatches,
          evidenceState: 'complete_joined',
        };
      }
    }
  }

  const evidenceState = metricMatches.length === 0 && statusMatches.length === 0
    ? 'none'
    : metricMatches.length === 0
      ? 'status_only'
      : statusMatches.length === 0
        ? 'metric_only'
        : 'separate_without_join';

  return {
    match: null,
    metricMatches,
    statusMatches,
    evidenceState,
  };
}

function findSharedJoinField(left: MetricStatusEvidenceSource, right: MetricStatusEvidenceSource): string | null {
  const rightFieldKeys = new Set(right.fields.map((field) => normalizeGeneratedFieldName(field.path)));
  const leftTimeField = left.fields.find((field) => isTimeEvidenceField(field) && rightFieldKeys.has(normalizeGeneratedFieldName(field.path)));
  if (leftTimeField) {
    return leftTimeField.path;
  }

  const leftKeyField = left.fields.find((field) => isStableKeyEvidenceField(field) && rightFieldKeys.has(normalizeGeneratedFieldName(field.path)));
  return leftKeyField?.path ?? null;
}

function toFieldAlias(field: MetricStatusEvidenceField): string {
  const parts = meaningfulFieldTokens(field.name);
  const fallback = normalizeGeneratedFieldName(field.name) || 'value';
  if (parts.length === 0) {
    return fallback;
  }

  return parts
    .map((part, index) => (index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join('');
}

function toSafeFieldAlias(field: MetricStatusEvidenceField): string {
  const alias = toFieldAlias(field);
  return FORBIDDEN_DYNAMIC_KEYS.has(alias) ? `${alias}Value` : alias;
}

function metricStatusPromptUsesTankLevelConcept(terms: MetricStatusPromptTerms): boolean {
  const promptTokens = new Set(meaningfulFieldTokens(terms.metricTerm));
  return promptTokens.has('level') && promptTokens.has('tank');
}

function getMetricStatusMetricAlias(
  match: MetricStatusFieldMatch,
  terms: MetricStatusPromptTerms,
): string {
  if (metricStatusPromptUsesTankLevelConcept(terms) && fieldLooksLikeTankLevelConcept(match.source, match.field)) {
    return 'Tank Level';
  }

  const normalizedFieldName = normalizeGeneratedFieldName(match.field.name);
  const normalizedFieldPath = normalizeGeneratedFieldName(match.field.path);
  if (normalizedFieldName === 'percentfull' || normalizedFieldPath === 'percentfull') {
    return 'Tank Level';
  }

  return toSafeFieldAlias(match.field);
}

function getMetricStatusStatusAlias(
  match: MetricStatusFieldMatch,
  terms: MetricStatusPromptTerms,
): string {
  const promptTokens = new Set(meaningfulFieldTokens(terms.statusTerm));
  const normalizedFieldName = normalizeGeneratedFieldName(match.field.name);
  const normalizedFieldPath = normalizeGeneratedFieldName(match.field.path);
  const statusFieldAliases = new Set(['status', 'state', 'valve', 'pumpstatus']);
  const promptSuggestsPumpStatus = promptTokens.has('pump') && (promptTokens.has('status') || promptTokens.has('state'));

  if (
    fieldLooksLikeStatusConcept(match.source, match.field) &&
    (
      promptSuggestsPumpStatus ||
      statusFieldAliases.has(normalizedFieldName) ||
      statusFieldAliases.has(normalizedFieldPath)
    )
  ) {
    return 'Status';
  }

  return toSafeFieldAlias(match.field);
}

function collectSampleValueCandidates(
  row: Record<string, unknown>,
  prefix?: string,
): Array<{ path: string; value: unknown }> {
  const candidates: Array<{ path: string; value: unknown }> = [];

  for (const [key, value] of Object.entries(row)) {
    if (FORBIDDEN_DYNAMIC_KEYS.has(key)) {
      continue;
    }

    const path = prefix ? `${prefix}.${key}` : key;
    candidates.push({ path, value });

    if ((isGenericRecordFieldName(key) || key === 'data' || key === 'metadata') && isRecord(value)) {
      candidates.push(...collectSampleValueCandidates(value, key === 'data' || key === 'metadata' ? path : prefix));
    }
  }

  return candidates;
}

function findSampleValueForField(response: QueryResponse, field: MetricStatusEvidenceField): unknown {
  const targetKeys = new Set([
    normalizeGeneratedFieldName(field.path),
    normalizeGeneratedFieldName(field.name),
  ]);

  for (const row of response.data ?? []) {
    if (!isRecord(row)) {
      continue;
    }

    for (const candidate of collectSampleValueCandidates(row)) {
      if (targetKeys.has(normalizeGeneratedFieldName(candidate.path))) {
        return candidate.value;
      }
    }
  }

  return undefined;
}

function getTemplateMeasurePreviewAliases(response: QueryResponse): string[] {
  return (response.templateQuery?.measures ?? []).flatMap((measure) => {
    const alias = measure.alias?.trim();
    if (alias) {
      return [alias];
    }

    const field = measure.field.trim();
    return field && !FORBIDDEN_DYNAMIC_KEYS.has(field) ? [field] : [];
  });
}

function synthesizedMetricStatusPreviewHasRequestedValues(response: QueryResponse): boolean {
  const aliases = getTemplateMeasurePreviewAliases(response);
  if (aliases.length === 0) {
    return false;
  }

  return (response.data ?? []).some((row) => {
    if (!isRecord(row)) {
      return false;
    }

    return aliases.every((alias) => isMeaningfulPreviewValue(row[alias]));
  });
}

function getSynthesizedMetricStatusPreviewIssue(
  response: QueryResponse,
  prompt: string,
  diagnostic: MetricStatusSynthesisDiagnostic | undefined,
): string | null {
  if (
    !promptSuggestsMetricWithCategoricalStatus(prompt) ||
    !templateQueryHasMetricAndStatus(response) ||
    synthesizedMetricStatusPreviewHasRequestedValues(response)
  ) {
    return null;
  }

  const explicitTimeRange = getMetricStatusPromptTimeRange(prompt);
  if (explicitTimeRange) {
    return diagnostic?.reason === 'synthesized'
      ? 'The synthesized metric/status template did not return preview values for both requested fields in the requested time range. Review the time range or rebuild before saving.'
      : 'The metric/status template did not return preview values for both requested fields in the requested time range. Review the time range or rebuild before saving.';
  }

  return diagnostic?.reason === 'synthesized'
    ? 'The synthesized metric/status template does not include preview values for both requested fields. Review the inferred field mapping before saving.'
    : 'The metric/status template does not include preview values for both requested fields. Review the field mapping before saving.';
}

function sourceIdFromSchema(source: MetricStatusEvidenceSource): string {
  const normalized = normalizeGeneratedFieldName(source.schemaId);
  return normalized || normalizeGeneratedFieldName(source.schemaName) || 'source';
}

function createMetricStatusSingleSourceFallback(
  prompt: string,
  response: QueryResponse,
  match: MetricStatusEvidenceMatch,
  terms: MetricStatusPromptTerms,
  diagnostic?: MetricStatusSynthesisDiagnostic,
): CreateTemplateResult {
  const timeField = match.metric.source.fields.find(isTimeEvidenceField);
  const promptTimeRange = timeField ? getMetricStatusPromptTimeRange(prompt) : null;
  const metricAlias = getMetricStatusMetricAlias(match.metric, terms);
  const statusAlias = getMetricStatusStatusAlias(match.status, terms);
  const timeSampleValue = timeField ? findSampleValueForField(response, timeField) : undefined;
  const metricSampleValue = findSampleValueForField(response, match.metric.field);
  const statusSampleValue = findSampleValueForField(response, match.status.field);
  const hasMetricSampleValue = isMeaningfulPreviewValue(metricSampleValue);
  const hasStatusSampleValue = isMeaningfulPreviewValue(statusSampleValue);
  const hasAnyMeasureSampleValue = hasMetricSampleValue || hasStatusSampleValue;
  const sampleRow: Record<string, unknown> = {};

  if (
    promptTimeRange &&
    timeField?.path &&
    !FORBIDDEN_DYNAMIC_KEYS.has(timeField.path) &&
    isMeaningfulPreviewValue(timeSampleValue)
  ) {
    sampleRow[timeField.path] = timeSampleValue;
  }
  sampleRow[metricAlias] = hasMetricSampleValue ? metricSampleValue : null;
  sampleRow[statusAlias] = hasStatusSampleValue ? statusSampleValue : null;

  const templateQuery: NonNullable<QueryResponse['templateQuery']> = {
    schemaId: match.metric.source.schemaId,
    groupId: match.metric.source.groupId,
    measures: [
      { field: match.metric.field.path, aggregation: 'avg', alias: metricAlias },
      { field: match.status.field.path, aggregation: 'last', alias: statusAlias },
    ],
    ...(timeField && promptTimeRange
      ? {
          groupBy: [{ field: timeField.path, granularity: promptTimeRange.granularity }],
          timeRange: { field: timeField.path, preset: promptTimeRange.preset },
          orderBy: [{ field: timeField.path, direction: 'asc' }],
        }
      : {}),
    limit: 1000,
  };
  const synthesizedResponse: QueryResponse = {
    ...cleanQueryResponseUserText(response),
    success: true,
    answer: 'Generated a reusable table template with metric and status fields.',
    error: undefined,
    suggestion: undefined,
    source: {
      id: match.metric.source.schemaId,
      name: match.metric.source.schemaName,
    },
    data: hasAnyMeasureSampleValue ? [sampleRow] : [],
    visualization: {
      type: 'table',
      config: {
        columns: [
          ...(promptTimeRange && timeField?.path ? [timeField.path] : []),
          metricAlias,
          statusAlias,
        ],
      },
    },
    templateQuery,
  };

  return {
    kind: 'template',
    response: synthesizedResponse,
    metadata: getSingleResultMetadata(synthesizedResponse, prompt, {
      metricStatusSynthesisDiagnostic: diagnostic,
    }),
  };
}

function createMetricStatusCompositeDefinition(
  prompt: string,
  match: MetricStatusEvidenceMatch,
): CompositeQueryDefinition | null {
  if (!match.joinField || match.metric.source.schemaId === match.status.source.schemaId) {
    return null;
  }

  const metricSourceId = sourceIdFromSchema(match.metric.source);
  const statusSourceId = sourceIdFromSchema(match.status.source);
  const metricAlias = toSafeFieldAlias(match.metric.field);
  const statusAlias = toSafeFieldAlias(match.status.field);
  const joinEvidenceField = match.metric.source.fields.find((field) => normalizeGeneratedFieldName(field.path) === normalizeGeneratedFieldName(match.joinField));
  const joinUsesTime = Boolean(joinEvidenceField && isTimeEvidenceField(joinEvidenceField));
  const promptTimeRange = joinUsesTime ? getMetricStatusPromptTimeRange(prompt) : null;
  const joinGranularity = promptTimeRange?.granularity ?? 'hour';
  const timeBucketGroupBy: Array<{ field: string; granularity: TimeGranularity }> = [
    { field: match.joinField, granularity: joinGranularity },
  ];
  const exactGroupBy: Array<{ field: string }> = [{ field: match.joinField }];
  const joinGroupBy = joinUsesTime
    ? timeBucketGroupBy
    : exactGroupBy;
  const joinTimeRange = joinUsesTime && promptTimeRange
    ? { field: match.joinField, preset: promptTimeRange.preset }
    : undefined;
  const joinKey: CompositeJoinKey = joinUsesTime
    ? { left: match.joinField, right: match.joinField, kind: 'time-bucket', granularity: joinGranularity }
    : { left: match.joinField, right: match.joinField, kind: 'exact' };

  return {
    sources: [
      {
        id: metricSourceId,
        label: match.metric.source.schemaName,
        query: {
          schemaId: match.metric.source.schemaId,
          groupId: match.metric.source.groupId,
          schemaName: match.metric.source.schemaName,
          measures: [{ field: match.metric.field.path, aggregation: 'avg', alias: metricAlias }],
          groupBy: joinGroupBy,
          ...(joinTimeRange ? { timeRange: joinTimeRange } : {}),
          orderBy: [{ field: match.joinField, direction: 'asc' }],
          limit: 1000,
        },
      },
      {
        id: statusSourceId,
        label: match.status.source.schemaName,
        query: {
          schemaId: match.status.source.schemaId,
          groupId: match.status.source.groupId,
          schemaName: match.status.source.schemaName,
          measures: [{ field: match.status.field.path, aggregation: 'last', alias: statusAlias }],
          groupBy: joinGroupBy,
          ...(joinTimeRange ? { timeRange: joinTimeRange } : {}),
          orderBy: [{ field: match.joinField, direction: 'asc' }],
          limit: 1000,
        },
      },
    ],
    primarySourceId: metricSourceId,
    joinType: 'left',
    joinKeys: [joinKey],
    fields: [
      { sourceId: metricSourceId, field: match.joinField, alias: match.joinField },
      { sourceId: metricSourceId, field: metricAlias, alias: metricAlias },
      { sourceId: statusSourceId, field: statusAlias, alias: statusAlias },
    ],
    orderBy: [{ field: match.joinField, direction: 'asc' }],
    limit: 1000,
  };
}

function singleResultAllowsMetricStatusFallback(response: QueryResponse, prompt: string): boolean {
  return (
    promptSuggestsMetricWithCategoricalStatus(prompt) &&
    !responseNeedsClarification(response) &&
    (
      responseHasMissingMeasureError(response) ||
      !hasTemplateQuery(response) ||
      singleTemplateIsGenericRecordFallback(response) ||
      responseTextSuggestsTemplateCannotBeGenerated(response)
    )
  );
}

function singleSourceFallbackIsIncompleteForMetricStatus(response: QueryResponse, prompt: string): boolean {
  return (
    promptSuggestsMetricWithCategoricalStatus(prompt) &&
    !responseNeedsClarification(response) &&
    hasTemplateQuery(response) &&
    !templateQueryHasMetricAndStatus(response)
  );
}

function getMetricStatusSynthesisMessage(reason: MetricStatusSynthesisReason): string {
  if (reason === 'no_metric_status_evidence') {
    return 'Deterministic recovery was attempted, but schema, debug, and sample evidence did not include both a usable numeric metric field and a status/state field.';
  }

  if (reason === 'no_shared_join_key_time') {
    return 'Deterministic recovery found metric and status fields in separate sources, but no shared timestamp or stable key for joining them.';
  }

  if (reason === 'composite_unusable') {
    return 'Deterministic recovery found metric/status evidence, but the generated composite result was unusable and no safe replacement template could be built.';
  }

  if (reason === 'synthesized') {
    return 'Deterministic metric/status recovery built a reusable template from schema, debug, or sample evidence.';
  }

  return 'Deterministic metric/status recovery was not attempted for this result.';
}

function createMetricStatusSynthesisDiagnostic(params: {
  attempted: boolean;
  reason: MetricStatusSynthesisReason;
  compositeState: MetricStatusCompositeState;
  evidenceState: MetricStatusEvidenceState;
}): MetricStatusSynthesisDiagnostic {
  return {
    ...params,
    message: getMetricStatusSynthesisMessage(params.reason),
  };
}

function attachMetricStatusSynthesisDiagnostic(
  result: CreateTemplateResult,
  diagnostic: MetricStatusSynthesisDiagnostic,
): CreateTemplateResult {
  if (result.kind === 'template') {
    return {
      ...result,
      metadata: {
        ...result.metadata,
        deterministicMetricStatusSynthesis: diagnostic,
      },
    };
  }

  if (result.kind === 'composite') {
    return {
      ...result,
      metadata: {
        ...result.metadata,
        deterministicMetricStatusSynthesis: diagnostic,
      },
    };
  }

  return {
    ...result,
    metadata: {
      ...result.metadata,
      deterministicMetricStatusSynthesis: diagnostic,
    },
  };
}

async function attemptDeterministicMetricStatusFallback(
  prompt: string,
  response: QueryResponse,
  dependencies: CreateTemplateGenerationDependencies,
  options: MetricStatusFallbackAttemptOptions = {},
): Promise<MetricStatusFallbackAttempt> {
  const compositeState = options.compositeState ?? 'not_attempted';
  const shouldAttempt =
    singleResultAllowsMetricStatusFallback(response, prompt) ||
    (options.allowIncompleteSingleSourceFallback === true && singleSourceFallbackIsIncompleteForMetricStatus(response, prompt));

  if (!shouldAttempt) {
    const diagnostic = createMetricStatusSynthesisDiagnostic({
      attempted: false,
      reason: 'synthesis_not_attempted',
      compositeState,
      evidenceState: 'not_checked',
    });
    logMetricStatusCreateResponseDiagnostic(prompt, response, diagnostic);

    return {
      result: null,
      diagnostic,
    };
  }

  const terms = getMetricStatusPromptTerms(prompt);
  if (!terms) {
    const diagnostic = createMetricStatusSynthesisDiagnostic({
      attempted: true,
      reason: 'no_metric_status_evidence',
      compositeState,
      evidenceState: 'none',
    });
    logMetricStatusCreateResponseDiagnostic(prompt, response, diagnostic);

    return {
      result: null,
      diagnostic,
    };
  }

  const evidenceSources = getMetricStatusEvidenceSources(response);
  const evidenceDiagnosis = diagnoseMetricStatusEvidenceMatch(evidenceSources, terms);
  if (!evidenceDiagnosis.match) {
    const reason = evidenceDiagnosis.evidenceState === 'separate_without_join'
      ? 'no_shared_join_key_time'
      : 'no_metric_status_evidence';
    const diagnostic = createMetricStatusSynthesisDiagnostic({
      attempted: true,
      reason,
      compositeState,
      evidenceState: evidenceDiagnosis.evidenceState,
    });
    logMetricStatusCreateResponseDiagnostic(prompt, response, diagnostic);

    return {
      result: null,
      diagnostic,
    };
  }

  const match = evidenceDiagnosis.match;
  if (match.metric.source === match.status.source) {
    const diagnostic = createMetricStatusSynthesisDiagnostic({
      attempted: true,
      reason: 'synthesized',
      compositeState,
      evidenceState: evidenceDiagnosis.evidenceState,
    });
    const result = attachMetricStatusSynthesisDiagnostic(
      createMetricStatusSingleSourceFallback(prompt, response, match, terms, diagnostic),
      diagnostic,
    );
    logMetricStatusCreateResponseDiagnostic(prompt, response, diagnostic);

    return {
      result,
      diagnostic,
    };
  }

  const definition = createMetricStatusCompositeDefinition(prompt, match);
  if (!definition) {
    const diagnostic = createMetricStatusSynthesisDiagnostic({
      attempted: true,
      reason: 'no_shared_join_key_time',
      compositeState,
      evidenceState: 'separate_without_join',
    });
    logMetricStatusCreateResponseDiagnostic(prompt, response, diagnostic);

    return {
      result: null,
      diagnostic,
    };
  }

  let previewRows: Record<string, unknown>[] = [];
  try {
    previewRows = await dependencies.previewComposite(definition);
  } catch (previewError) {
    console.warn('[DataDiscoveryModal] Failed to preview deterministic metric/status query', {
      message: getSafePreviewErrorMessage(previewError),
    });
  }

  const diagnostic = createMetricStatusSynthesisDiagnostic({
    attempted: true,
    reason: 'synthesized',
    compositeState,
    evidenceState: evidenceDiagnosis.evidenceState,
  });
  logMetricStatusCreateResponseDiagnostic(prompt, response, diagnostic);

  return {
    result: {
      kind: 'composite',
      explanation: 'Generated a reusable table template with metric and status fields.',
      definition,
      previewRows,
      metadata: {
        ...getCompositeResultMetadata(definition, prompt, false, undefined, previewRows),
        deterministicMetricStatusSynthesis: diagnostic,
      },
    },
    diagnostic,
  };
}

function isGenericRecordFieldName(value: string | null | undefined): boolean {
  const normalized = normalizeGeneratedFieldName(value);
  return normalized === 'record' || normalized === 'raw' || normalized === 'document' || normalized === 'rawdocument';
}

function isGenericRawRecordMeasure(measure: NonNullable<QueryResponse['templateQuery']>['measures'][number]): boolean {
  return measure.aggregation === 'first' && measure.field.trim() === '*';
}

function singleTemplateIsGenericRecordFallback(response: QueryResponse): boolean {
  const measures = response.templateQuery?.measures ?? [];
  if (measures.length === 0) {
    return false;
  }

  const hasMeaningfulProjectedMeasure = measures.some((measure) => !isGenericRawRecordMeasure(measure));
  if (!hasMeaningfulProjectedMeasure && measures.every(isGenericRawRecordMeasure)) {
    return true;
  }

  const rawRecordMeasureAliases = measures
    .filter(isGenericRawRecordMeasure)
    .map((measure) => measure.alias)
    .filter((alias): alias is string => typeof alias === 'string');
  if (rawRecordMeasureAliases.length === 0 || response.data?.length === 0) {
    return false;
  }

  return (response.data ?? []).every((row) => {
    if (!isRecord(row)) {
      return false;
    }

    const rowKeys = Object.keys(row);
    return (
      rowKeys.length > 0 &&
      rowKeys.every((key) => rawRecordMeasureAliases.includes(key) || isGenericRecordFieldName(key))
    );
  });
}

function getMissingMeasureReviewReason(response: QueryResponse, prompt: string): string | null {
  if (!responseHasMissingMeasureError(response)) {
    return null;
  }

  if (promptSuggestsMetricWithCategoricalStatus(prompt)) {
    return 'The prompt includes both a numeric metric and a status/state field, but the generator did not return a reusable numeric measure. Review the field mapping or rebuild before saving.';
  }

  if (promptSuggestsCategoricalStatusWithoutMetric(prompt)) {
    return 'The prompt only names a status/state field. Choose a numeric measure or rebuild as a table-style output before saving.';
  }

  return 'The generator did not identify a numeric measure for this request. Review the requested fields before saving.';
}

function shouldSuppressSingleResultError(
  response: QueryResponse,
  metadata: CreateResultMetadata,
): boolean {
  return metadata.needsReview && responseHasMissingMeasureError(response);
}

function getCombinedSourceSingleFallbackReviewReason(
  diagnostic: MetricStatusSynthesisDiagnostic | undefined,
): string {
  if (!diagnostic || !diagnostic.attempted) {
    return COMBINED_SOURCE_SINGLE_FALLBACK_REVIEW_REASON;
  }

  if (diagnostic.reason === 'no_metric_status_evidence') {
    return 'A combined-source result could not be generated. Deterministic recovery was attempted, but schema/debug/sample evidence did not include both a usable numeric metric field and a status/state field. Review field evidence or rebuild before saving.';
  }

  if (diagnostic.reason === 'no_shared_join_key_time') {
    return 'A combined-source result could not be generated. Metric/status evidence was found in separate sources, but no shared timestamp or stable key was available for joining them. Review the join evidence or rebuild before saving.';
  }

  if (diagnostic.reason === 'composite_unusable') {
    return 'A combined-source result could not be generated. Metric/status evidence was found, but the generated composite result was unusable and deterministic recovery could not build a safe replacement. Review or rebuild before saving.';
  }

  return COMBINED_SOURCE_SINGLE_FALLBACK_REVIEW_REASON;
}

function getMetricStatusRawRecordReviewReason(
  diagnostic: MetricStatusSynthesisDiagnostic | undefined,
): string {
  if (!diagnostic) {
    return GENERIC_RECORD_FALLBACK_REVIEW_REASON;
  }

  if (!diagnostic.attempted) {
    return 'The generated template returned a raw record, and deterministic metric/status recovery was not attempted for this result. Review or rebuild before saving.';
  }

  if (diagnostic.reason === 'no_metric_status_evidence') {
    if (diagnostic.evidenceState === 'metric_only') {
      return 'Deterministic metric/status recovery was attempted, but no usable status/state field evidence was found. Review schema, debug, or sample evidence before saving.';
    }

    if (diagnostic.evidenceState === 'status_only') {
      return 'Deterministic metric/status recovery was attempted, but no usable numeric metric field evidence was found. Review schema, debug, or sample evidence before saving.';
    }

    return 'Deterministic metric/status recovery was attempted, but no usable numeric metric field evidence or status/state field evidence was found. Review schema, debug, or sample evidence before saving.';
  }

  if (diagnostic.reason === 'no_shared_join_key_time') {
    return 'Deterministic metric/status recovery found metric and status field evidence, but no shared timestamp or stable key was found for joining them. Review join evidence before saving.';
  }

  if (diagnostic.reason === 'composite_unusable') {
    return 'Deterministic metric/status recovery found metric/status evidence, but no preview rows contained both metric and status values. Review the generated query before saving.';
  }

  return GENERIC_RECORD_FALLBACK_REVIEW_REASON;
}

function getSingleResultMetadata(
  response: QueryResponse,
  prompt: string,
  options: { metricStatusSynthesisDiagnostic?: MetricStatusSynthesisDiagnostic } = {},
): CreateResultMetadata {
  const requestedIntentCategory = getRequestedIntentCategory(prompt, response);

  if (!response.templateQuery) {
    const needsClarification = responseNeedsClarification(response);
    const missingMeasureReviewReason = getMissingMeasureReviewReason(response, prompt);
    const reason = needsClarification
      ? 'The request needs clarification before it can become a reusable template.'
      : missingMeasureReviewReason ?? 'The request did not produce a reusable template query.';

    return {
      resultTypeLabel: needsClarification ? 'Needs Clarification' : 'Needs Review',
      intentCategory: needsClarification ? 'needs_clarification' : 'needs_review',
      requestedIntentCategory,
      needsReview: !needsClarification,
      needsClarification,
      reviewReason: needsClarification ? undefined : reason,
      clarificationReason: needsClarification ? reason : undefined,
      saveDisabledReason: reason,
    };
  }

  const topConfidence = getTopMatchedSourceConfidence(response);
  const confidenceIsUnclear = topConfidence !== null && topConfidence < 0.55;
  const promptNeedsPeriodComparison = textSuggestsPeriodComparison(prompt) || textSuggestsOpenEndedPeriodComparison(prompt);
  const promptHasSingleSourceMetricStatus = promptSuggestsMetricWithCategoricalStatus(prompt) && templateQueryHasMetricAndStatus(response);
  const promptNeedsComposite = textSuggestsCompositeIntent(prompt) && !promptHasSingleSourceMetricStatus;
  const promptNeedsUnsupportedDerivedMetric = textSuggestsDerivedMetric(prompt);
  const backendSaysCannotGenerate = responseTextSuggestsTemplateCannotBeGenerated(response);
  const isGenericRecordFallback = singleTemplateIsGenericRecordFallback(response);

  if (responseNeedsClarification(response)) {
    const clarificationReason = 'The request needs a source choice before it can become a reusable template.';
    return {
      resultTypeLabel: 'Needs Clarification',
      shapeLabel: 'Single Source',
      intentCategory: 'needs_clarification',
      requestedIntentCategory,
      needsReview: false,
      needsClarification: true,
      clarificationReason,
      saveDisabledReason: clarificationReason,
    };
  }

  const synthesizedMetricStatusPreviewIssue = getSynthesizedMetricStatusPreviewIssue(
    response,
    prompt,
    options.metricStatusSynthesisDiagnostic,
  );
  if (synthesizedMetricStatusPreviewIssue) {
    return {
      resultTypeLabel: 'Needs Review',
      shapeLabel: 'Single Source',
      intentCategory: 'needs_review',
      requestedIntentCategory,
      needsReview: true,
      needsClarification: false,
      reviewAcceptanceAllowed: false,
      reviewReason: synthesizedMetricStatusPreviewIssue,
      saveDisabledReason: synthesizedMetricStatusPreviewIssue,
      deterministicMetricStatusSynthesis: options.metricStatusSynthesisDiagnostic,
    };
  }

  if (isGenericRecordFallback) {
    const reviewReason = getMetricStatusRawRecordReviewReason(options.metricStatusSynthesisDiagnostic);
    return {
      resultTypeLabel: 'Needs Review',
      shapeLabel: 'Single Source',
      intentCategory: 'needs_review',
      requestedIntentCategory,
      needsReview: true,
      needsClarification: false,
      reviewAcceptanceAllowed: false,
      reviewReason,
      saveDisabledReason: reviewReason,
      deterministicMetricStatusSynthesis: options.metricStatusSynthesisDiagnostic,
    };
  }

  if ((promptNeedsPeriodComparison || promptNeedsComposite || promptNeedsUnsupportedDerivedMetric) && !singleTemplateSupportsPeriodComparison(response)) {
    const reviewReason = promptNeedsPeriodComparison
      ? 'A valid period comparison could not be generated. No preview is shown because the fallback result would not represent the requested change. Review or rebuild before saving.'
      : promptNeedsUnsupportedDerivedMetric
        ? 'A valid derived metric could not be generated. No preview is shown because the fallback result would not represent the requested calculation. Review or rebuild before saving.'
        : getCombinedSourceSingleFallbackReviewReason(options.metricStatusSynthesisDiagnostic);

    return {
      resultTypeLabel: 'Needs Review',
      shapeLabel: 'Single Source',
      intentCategory: 'needs_review',
      requestedIntentCategory,
      needsReview: true,
      needsClarification: false,
      reviewAcceptanceAllowed: promptNeedsComposite ? false : undefined,
      reviewReason,
      saveDisabledReason: reviewReason,
      deterministicMetricStatusSynthesis: options.metricStatusSynthesisDiagnostic,
    };
  }

  if (backendSaysCannotGenerate) {
    const reviewReason = 'The backend response says a reusable template cannot be generated from this request. Review the requested source names and available data before saving.';
    return {
      resultTypeLabel: 'Needs Review',
      shapeLabel: 'Single Source',
      intentCategory: 'needs_review',
      requestedIntentCategory,
      needsReview: true,
      needsClarification: false,
      reviewReason,
      saveDisabledReason: reviewReason,
    };
  }

  return {
    resultTypeLabel: confidenceIsUnclear ? 'Needs Review' : 'Single Source',
    shapeLabel: confidenceIsUnclear ? undefined : 'Single Source',
    intentCategory: confidenceIsUnclear ? 'needs_review' : requestedIntentCategory,
    requestedIntentCategory,
    needsReview: confidenceIsUnclear,
    needsClarification: false,
    reviewReason: confidenceIsUnclear ? 'The best matching data source has low confidence. Review the schema and fields before saving.' : undefined,
  };
}

function shouldSuppressSingleSourceFallbackPreview(metadata: CreateResultMetadata): boolean {
  return (
    metadata.needsReview &&
    (
      metadata.reviewAcceptanceAllowed === false ||
      metadata.requestedIntentCategory === 'period_comparison' ||
      metadata.requestedIntentCategory === 'multi_source_combined' ||
      metadata.requestedIntentCategory === 'derived_metric'
    )
  );
}

function toReviewOnlySingleSourceResponse(response: QueryResponse): QueryResponse {
  return {
    ...cleanQueryResponseUserText(response),
    answer: undefined,
    data: [],
    templateQuery: undefined,
    templateVariables: undefined,
    visualization: undefined,
  };
}

export function getCompositeResultMetadata(
  definition: CompositeQueryDefinition,
  prompt: string,
  needsReview: boolean,
  reviewReason?: string,
  previewRows: Record<string, unknown>[] = [],
): CreateResultMetadata {
  const shapeLabel = getCompositeShapeLabel(definition);
  const requestedIntentCategory = getCompositeIntentCategory(definition, prompt);
  const promptNeedsPeriodComparison = textSuggestsPeriodComparison(prompt) || textSuggestsOpenEndedPeriodComparison(prompt);
  const promptNeedsDerivedMetric = textSuggestsDerivedMetric(prompt);
  const definitionIsPeriodComparison = definitionSuggestsPeriodComparison(definition);
  const periodComparisonMismatch = promptNeedsPeriodComparison && !definitionIsPeriodComparison;
  const derivedMetricMismatch = promptNeedsDerivedMetric && !compositeDefinitionHasDerivedOutput(definition);
  const previewIssue = getCompositePreviewIssue(definition, previewRows, { requireDerivedOutput: promptNeedsDerivedMetric });
  const sourceIdentityReviewReason = getAiCompositeSourceIdentityReviewReason(definition);
  const resultNeedsReview = Boolean(
    needsReview ||
    periodComparisonMismatch ||
    derivedMetricMismatch ||
    previewIssue !== null ||
    sourceIdentityReviewReason,
  );
  const polishedReviewReason = polishCompositeReviewReason(reviewReason, definition, prompt);
  const shapeReviewReason =
    previewIssue ??
    (periodComparisonMismatch
      ? 'The prompt asks for a period comparison, but the generated composite definition does not include distinct comparison periods. Review or rebuild it before saving.'
      : derivedMetricMismatch
        ? 'The prompt asks for a derived metric, but the generated composite definition does not include a computed field. Review or rebuild it before saving.'
        : polishedReviewReason);
  const resolvedReviewReason = mergeReviewReasons(sourceIdentityReviewReason ?? undefined, shapeReviewReason);
  const shapeSaveDisabledReason =
    previewIssue ??
    (periodComparisonMismatch ? 'The generated result does not include the requested period comparison.' : undefined) ??
    (derivedMetricMismatch ? 'The generated result does not include the requested derived metric.' : undefined) ??
    (resultNeedsReview
      ? shapeReviewReason ?? 'The generated multi-source template needs review before it can be saved.'
      : undefined);
  const saveDisabledReason = mergeReviewReasons(sourceIdentityReviewReason ?? undefined, shapeSaveDisabledReason);

  return {
    resultTypeLabel: resultNeedsReview ? 'Needs Review' : shapeLabel,
    shapeLabel,
    intentCategory: resultNeedsReview ? 'needs_review' : requestedIntentCategory,
    requestedIntentCategory,
    needsReview: resultNeedsReview,
    needsClarification: false,
    reviewAcceptanceAllowed: false,
    reviewReason: resolvedReviewReason,
    saveDisabledReason,
  };
}

function shouldUseCompositeResult(
  singleResult: QueryResponse | null,
  compositeResult: CompositeGenerationResponse | null,
  prompt: string,
): boolean {
  const definition = compositeResult?.compositeQueryDefinition;
  if (!isUsableCompositeDefinition(definition)) {
    return false;
  }

  if (promptSuggestsMetricWithCategoricalStatus(prompt) && templateQueryHasMetricAndStatus(singleResult)) {
    return false;
  }

  if (!hasTemplateQuery(singleResult) || hasQueryResponseError(singleResult) || responseNeedsClarification(singleResult)) {
    return true;
  }

  if (
    definitionSuggestsPeriodComparison(definition) ||
    textSuggestsCompositeIntent(prompt) ||
    promptSuggestsAmbiguousMetricRouting(prompt) ||
    (textSuggestsDerivedMetric(prompt) && compositeDefinitionHasDerivedOutput(definition))
  ) {
    return true;
  }

  const projectedSourceCount = getProjectedCompositeSourceCount(definition);
  if (projectedSourceCount >= 2 && singleResultHasAmbiguousSourceMatch(singleResult)) {
    return true;
  }

  const topConfidence = getTopMatchedSourceConfidence(singleResult);
  return topConfidence !== null && topConfidence < 0.55 && projectedSourceCount >= 2 && hasDistinctCompositeInputs(definition);
}

function shouldAttemptCompositeGeneration(prompt: string, singleResult: QueryResponse | null): boolean {
  if (promptSuggestsMetricWithCategoricalStatus(prompt) && templateQueryHasMetricAndStatus(singleResult)) {
    return false;
  }

  return (
    textSuggestsCompositeIntent(prompt) ||
    textSuggestsDerivedMetric(prompt) ||
    promptSuggestsAmbiguousMetricRouting(prompt) ||
    singleResultNeedsCompositeFallback(singleResult)
  );
}

function compositeDefinitionHasNoJoinKey(definition: CompositeQueryDefinition | undefined): boolean {
  return Boolean(definition && definition.sources.length >= 2 && definition.joinKeys.length === 0);
}

function getMetricStatusCompositeState(
  compositeSettled: Settled<CompositeGenerationResponse> | null,
  compositeResult: CompositeGenerationResponse | null,
  rawDefinition: CompositeQueryDefinition | undefined,
  normalizedDefinition: CompositeQueryDefinition | undefined,
): MetricStatusCompositeState {
  if (!compositeSettled) {
    return 'not_attempted';
  }

  if (compositeSettled.status === 'rejected') {
    return 'failed';
  }

  if (!rawDefinition) {
    return 'missing_definition';
  }

  if (!hasCompositeResponse(compositeResult) || !isUsableCompositeDefinition(normalizedDefinition)) {
    return 'unusable_definition';
  }

  return 'usable_not_selected';
}

function getMetricStatusNoJoinReviewMetadata(prompt: string, response: QueryResponse | null): CreateResultMetadata {
  const reviewReason =
    'The metric and status fields appear to come from separate sources, but no shared timestamp or stable key was generated for joining them. Review or rebuild before saving.';

  return {
    resultTypeLabel: 'Needs Review',
    shapeLabel: 'Combined Sources',
    intentCategory: 'needs_review',
    requestedIntentCategory: getRequestedIntentCategory(prompt, response ?? undefined),
    needsReview: true,
    needsClarification: false,
    reviewAcceptanceAllowed: false,
    reviewReason,
    saveDisabledReason: reviewReason,
  };
}

export async function generateCreateTemplateResult(
  prompt: string,
  dependencies: CreateTemplateGenerationDependencies,
  options: CreateTemplateGenerationOptions = {},
): Promise<{ result: CreateTemplateResult | null; error: string | null }> {
  const deterministicClarification = createUsageChangeBaselineClarification(prompt);
  if (deterministicClarification) {
    const response = createDeterministicClarificationQueryResponse(deterministicClarification);

    if ((options.clarificationDepth ?? 0) >= (options.maxClarificationDepth ?? 1)) {
      return {
        result: {
          kind: 'template',
          response,
          metadata: getClarificationLoopReviewMetadata(deterministicClarification),
        },
        error: null,
      };
    }

    return {
      result: {
        kind: 'clarification',
        response,
        clarification: deterministicClarification,
        metadata: getClarificationResultMetadata(deterministicClarification),
      },
      error: null,
    };
  }

  const singleSettled = await settle(dependencies.generateSingleSource(prompt));
  const singleResult = getFulfilledValue(singleSettled);

  if (singleResult && responseNeedsClarification(singleResult)) {
    const clarification = normalizeClarificationMetadata(singleResult, prompt);
    if ((options.clarificationDepth ?? 0) >= (options.maxClarificationDepth ?? 1)) {
      return {
        result: {
          kind: 'template',
          response: singleResult,
          metadata: getClarificationLoopReviewMetadata(clarification),
        },
        error: null,
      };
    }

    return {
      result: {
        kind: 'clarification',
        response: singleResult,
        clarification,
        metadata: getClarificationResultMetadata(clarification),
      },
      error: null,
    };
  }

  let compositeSettled: Settled<CompositeGenerationResponse> | null = null;
  if (shouldAttemptCompositeGeneration(prompt, singleResult)) {
    compositeSettled = await settle(dependencies.generateComposite(prompt));
  }

  const compositeResult = compositeSettled ? getFulfilledValue(compositeSettled) : null;
  const compositeSingleSourceResult = compositeResult?.singleSourceResult ?? null;
  const rawCompositeDefinition = compositeResult?.compositeQueryDefinition;
  const compositeDefinition = rawCompositeDefinition
    ? normalizePeriodComparisonAlignment(rawCompositeDefinition, prompt)
    : undefined;
  const metricStatusCompositeState = getMetricStatusCompositeState(
    compositeSettled,
    compositeResult,
    rawCompositeDefinition,
    compositeDefinition,
  );

  if (hasCompositeResponse(compositeResult) && compositeNeedsClarification(compositeResult)) {
    const clarification = normalizeCompositeClarificationMetadata(compositeResult, prompt);
    const response = createCompositeClarificationQueryResponse(clarification);

    if ((options.clarificationDepth ?? 0) >= (options.maxClarificationDepth ?? 1)) {
      return {
        result: {
          kind: 'template',
          response,
          metadata: getClarificationLoopReviewMetadata(clarification),
        },
        error: null,
      };
    }

    return {
      result: {
        kind: 'clarification',
        response,
        clarification,
        metadata: getClarificationResultMetadata(clarification),
      },
      error: null,
    };
  }

  if (compositeSingleSourceResult) {
    const metadata = getSingleResultMetadata(compositeSingleSourceResult, prompt);

    return {
      result: {
        kind: 'template',
        response: cleanQueryResponseUserText(compositeSingleSourceResult),
        metadata,
      },
      error: shouldSuppressSingleResultError(compositeSingleSourceResult, metadata)
        ? null
        : getQueryResponseErrorMessage(compositeSingleSourceResult),
    };
  }

  if (
    hasCompositeResponse(compositeResult) &&
    shouldUseCompositeResult(singleResult, compositeResult, prompt) &&
    isUsableCompositeDefinition(compositeDefinition)
  ) {
    let previewRows: Record<string, unknown>[] = [];

    try {
      previewRows = await dependencies.previewComposite(compositeDefinition);
    } catch (previewError) {
      console.warn('[DataDiscoveryModal] Failed to preview generated multi-source query', {
        message: getSafePreviewErrorMessage(previewError),
      });
    }

    const markerReviewReason = getReviewReasonFromExplanationMarker(compositeResult.explanation);
    const compositeNeedsReview = compositeResult.needsReview === true || Boolean(markerReviewReason);
    const singleErrorRequiresReview = Boolean(singleResult?.error) && !(
      singleResult &&
      responseHasMissingMeasureError(singleResult) &&
      promptSuggestsMetricWithCategoricalStatus(prompt)
    );
    const needsReview = !singleResult || singleErrorRequiresReview || compositeNeedsReview;
    return {
      result: {
        kind: 'composite',
        explanation: cleanUserFacingModelText(compositeResult.explanation, GENERIC_REVIEW_EXPLANATION),
        definition: compositeDefinition,
        previewRows,
        metadata: getCompositeResultMetadata(
          compositeDefinition,
          prompt,
          needsReview,
          compositeNeedsReview
            ? compositeResult.reviewReason ?? markerReviewReason ?? 'The generated multi-source template needs review before it can be saved.'
            : needsReview
              ? 'The single-source generator could not produce a confident template. Review the generated sources before saving.'
              : undefined,
          previewRows,
        ),
      },
      error: null,
    };
  }

  if (singleResult) {
    const deterministicMetricStatusFallback = await attemptDeterministicMetricStatusFallback(
      prompt,
      singleResult,
      dependencies,
      {
        allowIncompleteSingleSourceFallback: metricStatusCompositeState !== 'not_attempted',
        compositeState: metricStatusCompositeState,
      },
    );
    if (deterministicMetricStatusFallback.result) {
      return {
        result: deterministicMetricStatusFallback.result,
        error: null,
      };
    }

    const metricStatusSynthesisDiagnostic = deterministicMetricStatusFallback.diagnostic;

    if (
      promptSuggestsMetricWithCategoricalStatus(prompt) &&
      compositeDefinitionHasNoJoinKey(compositeDefinition) &&
      responseHasMissingMeasureError(singleResult)
    ) {
      const noJoinDiagnostic = createMetricStatusSynthesisDiagnostic({
        attempted: true,
        reason: 'no_shared_join_key_time',
        compositeState: metricStatusCompositeState,
        evidenceState: 'separate_without_join',
      });

      return {
        result: {
          kind: 'template',
          response: toReviewOnlySingleSourceResponse(singleResult),
          metadata: {
            ...getMetricStatusNoJoinReviewMetadata(prompt, singleResult),
            deterministicMetricStatusSynthesis: noJoinDiagnostic,
          },
        },
        error: null,
      };
    }

    const metadata = getSingleResultMetadata(singleResult, prompt, { metricStatusSynthesisDiagnostic });

    return {
      result: {
        kind: 'template',
        response: shouldSuppressSingleSourceFallbackPreview(metadata)
          ? toReviewOnlySingleSourceResponse(singleResult)
          : cleanQueryResponseUserText(singleResult),
        metadata,
      },
      error: shouldSuppressSingleResultError(singleResult, metadata)
        ? null
        : getQueryResponseErrorMessage(singleResult),
    };
  }

  if (hasCompositeResponse(compositeResult) && isUsableCompositeDefinition(compositeDefinition)) {
    let previewRows: Record<string, unknown>[] = [];

    try {
      previewRows = await dependencies.previewComposite(compositeDefinition);
    } catch (previewError) {
      console.warn('[DataDiscoveryModal] Failed to preview generated multi-source query', {
        message: getSafePreviewErrorMessage(previewError),
      });
    }

    const markerReviewReason = getReviewReasonFromExplanationMarker(compositeResult.explanation);
    return {
      result: {
        kind: 'composite',
        explanation: cleanUserFacingModelText(compositeResult.explanation, GENERIC_REVIEW_EXPLANATION),
        definition: compositeDefinition,
        previewRows,
        metadata: getCompositeResultMetadata(
          compositeDefinition,
          prompt,
          true,
          compositeResult.reviewReason ?? markerReviewReason ?? 'Only the multi-source generator returned a structured result. Review it before saving.',
          previewRows,
        ),
      },
      error: null,
    };
  }

  return {
    result: null,
    error: getRejectedMessage(singleSettled) ?? (compositeSettled ? getRejectedMessage(compositeSettled) : null) ?? 'Failed to generate a reusable template',
  };
}

export function getDisplayResultTypeLabel(metadata: CreateResultMetadata): CreateResultTypeLabel {
  return metadata.needsReview ? metadata.resultTypeLabel : metadata.shapeLabel ?? metadata.resultTypeLabel;
}
